/* eslint-disable eqeqeq */
/**
 * Phase 6 Vendor-Port W2 — GAS HEATER SELECTOR (§33.5).
 *
 * React port of components/gas_heater/view/gas_heater.selector.js (2,646) + its
 * 4 templates, into the 6e2c component-selector chassis (§30) as the
 * 'gas_heater' tab body. BOARD only (header li.hide). Opened via the gas_heater
 * FORM's rating label: GasHeaterForm's FormHeading(withRatingBlock,
 * ratingBlockDataName='gas_heater') → api.openRatingStub() →
 * rating_mode:activate {name:'gas_heater', current_model:{id}, section} (the
 * §30.4 fan-out key is gas_heater_<id>:selector:<action>). gas_heater IS
 * reachable on OLD (withRating:true + navy hasRating).
 *
 * GAS HEATER "RATING" IS A SERVER-SIDE CMS CATALOG SEARCH (NOT an external
 * vendor /rating/ dispatch — §32 bypassed):
 *   - RATE = the availability search + a model.save PATCH (rate:true). For
 *     type-3 (direct-fire, burner-assy config 1) the legacy first POSTs
 *     ahu/gas_heaters/burner/ {rate:true} per burner config, then searches.
 *   - SELECT = searchGasHeaters(..., isSelect:true): the SAME model.save with
 *     rate:false + isSelect:true (a re-search + persist), then
 *     validate:tunnel:components:only → remove.
 *   - PRINT = the ONLY celery flow: POST /api/v1.0/rating/pdf_rating/ →
 *     {task_id} → socket `data` → window.open(report). [NEG]#79 FIXED: the
 *     legacy socket guard `if (celery_task_id, data.task_id)` is a comma
 *     operator (fires on ANY task); here we use REAL equality
 *     `data.task_id === celery_task_id`.
 *
 * ★ DATA-SAFETY (§4 + the 6e2a userArmed gate — HARD REQUIREMENT, the prompt's
 *   ★ block): gas_heater WRITES are Rate (CMS search + a burner POST), Select
 *   (model.save), Print (celery PDF). NOTHING fires on mount / open / tab-switch
 *   / browse. The RATE/SELECT/PRINT paths are armed ONLY by a real user
 *   #rate/#select/#print click (userArmed ref). Smoke = open / render / cancel
 *   only → 0 mutating calls. The legacy view ALSO fires writes on read in some
 *   branches (rateBurner on init when burnerAssy present, the type-3
 *   render-time rate) — we DO NOT replicate those; the React selector renders
 *   the STORED state (the form's withoutRatingSeparateAPI path) with zero
 *   writes. The code-only Rate/Select/Print write paths are verified by code
 *   review and parked to throwaway-AHU QA.
 *
 * WAVE-2 SCOPE NOTES (documented in the build report DEVIATIONS):
 *   - The full param-chain DEPENDENCY CASCADE (getCollections walking
 *     mfg→type→series→heatex/venting/pilot/config/fuel/firing/burner with
 *     auto-pick + capacity → output_rating_std → tube_rows recompute) is NOT
 *     reproduced as a live graph. Read-only we seed each select from its SAVED
 *     value + the param-GET list for the saved parents (the saved value always
 *     renders). The live re-cascade fires only when the user changes a parent +
 *     rates — unreachable read-only on dev3. Deferred to W3's dependency engine
 *     (the same DependenciesHandler reducer ewheel/phx/fan will share).
 *   - The Bypass tab's stub markup (duplicate airside_enclosure_id ids) +
 *     Options tab (rating_mfg housings) are placeholder-grade in legacy; ported
 *     as read-only echoes, not live.
 *   - type-3 direct-fire burner-config BUILDER (add/remove burner rows, the
 *     burner POST loop) is wired in the API + Rate payload but the interactive
 *     builder UI is STUBBED — all dev3 gas heaters + both test AHUs are type 1.
 *     Deferred to write-QA.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import { useGetAhuBoardProjectQuery } from 'app/services/ahuBoardApi';
import { getSocket, type SocketDataMessage } from 'app/socketClient';
import { optionLabel, type OptionRow } from '../formKit';
import { formatDecimalDisplay, formatIntegerDisplay } from '../formUtils';
import {
  ahuComponentFormsApi,
  useGetComponentDetailQuery,
  useGetComponentCmsDocQuery,
  useGetMasterListByUrlQuery,
  type IMasterRow,
} from 'app/services/ahuComponentFormsApi';
import { pendingSelectWrites, selectWriteKey, trackSelectWrites } from './selectWriteGate';
import {
  GH_CMS_DOC_URL,
  ghCmsOptionList,
  ghCmsSelectedLabel,
  type GasHeaterCmsDoc,
} from './gasHeaterCms';
import {
  GH_ALL_COUNT_URL,
  GH_DEFAULTS,
  GH_MFG_188,
  GH_BURNER_ASSY_USER_DEFINED,
  GH_BURNER_ASSY_DEPS,
  GH_REQUIRED_FIELDS,
  type BurnerRow,
  GH_COLLECTION_URLS,
  ghCatalogUrl,
  ghCountFilters,
  ghCountUrl,
  ghParamUrls,
} from './gasHeaterSelectorData';
import {
  useGetGasHeaterCountQuery,
  useLazyGetGasHeaterCatalogQuery,
  useLazyGetGasHeaterBurnersQuery,
  useLazyGetGhBurnerAssyQuery,
  useDeleteGasHeaterBurnerMutation,
  useRateGasHeaterMutation,
  useRequestGasHeaterPdfMutation,
  type GhRatePayload,
  type IGasHeaterRow,
} from './gasHeaterSelectorApi';
import { GasHeaterRatedOutputs } from './GasHeaterRatedOutputs';
import type { ComponentFormShim } from '../componentFormTypes';
import type { SelectorTabProps } from './selectorTypes';
import { guardSelect } from '../cascadeCommit';
import { takeSectionPendingRerate } from '../../psychrometric/pendingRerate';
import { useAppDispatch } from 'app/hooks';
import { queueSectionPatch } from '../sectionPatchQueue';
import { cmsSeasonsByComponent, deriveSeasonValue } from '../cmsDoc';
import type { ICmsAhuDoc } from 'models';
import { track } from 'app/analytics';

/** GLOBAL cms doc (legacy fn['current.cms'] — air.air_stream + weather_rating).
 * The CMS doc endpoint REQUIRES the construction_class_id query param (legacy
 * model/cms.js url(): `…/ahu/?construction_class_id=<n>`); without it the API
 * returns HTTP 400 "Invalid data" and the air_stream/weather_rating selects
 * render the raw id instead of the label. construction_class 1 is the default
 * AHU class and carries the full air_stream + weather_rating lists. */
const GLOBAL_CMS_URL = '/api/v1.0/cms/1/v1.0.0/ahu/?construction_class_id=1';

type TabName =
  | 'Heater'
  | 'Performance'
  | 'Mounting'
  | 'Options'
  | 'Bypass'
  | 'Pricing';

/** The legacy pane ids (`{{prefix}}Gas_Heater_<X>`, prefix `ahu-` on the
 * board). No stylesheet keys off them, but keeping them makes the rendered
 * tree diffable against the Backbone one. */
const PANE_ID: Record<TabName, string> = {
  Heater: 'ahu-Gas_Heater_Properties',
  Performance: 'ahu-Gas_Heater_Performance',
  Mounting: 'ahu-Gas_Heater_Mounting',
  Options: 'ahu-Gas_Heater_Options',
  Bypass: 'ahu-Gas_Heater_Bypass',
  Pricing: 'ahu-Gas_Heater_Pricing',
};

/** The Heater tab's three sub-tabs (tmpl:101-119, `ul.more_tabing`). */
type SubTabName = 'properties_more' | 'burner_more' | 'heat_exchanger_more';

/**
 * The `model_data.params_gas_heater` half of the flat `state.current`, i.e.
 * every key whose control carries `class="params_gas_heater"` in
 * gas_heater.selector.tmpl. Used ONLY by `doReset` (gas_heater.selector.js:1578
 * `model_data.params_gas_heater = {...gasHeaterDefaults, mfg_id}`), which must
 * drop exactly this half and leave the `model_field` half — Airflow, EADB,
 * desired LADB, Gas Inlet Pressure, Pricing, Assy Height/Distance, the burner
 * assembly and the staged season — untouched.
 *
 * Template lines, in order: mfg_id :129, type_id :147, series_id :164,
 * power_id :203, weather_rating_id :223, certification_id :243, venting_id
 * :268, conn_hand_id :290, heatex_mtl_id :315, tube_rows_vertically :355,
 * configuration :372, heatex_qty_id :389, burner_mtl_id :449, fuel_type_id
 * :469, firing_mode_id :489, pilot_id :536, qty_wide :803, qty_high :810,
 * output_rating_std :828/:850, blank_off_id :1023. `burner_config_id` has no
 * live select left in the template (both are commented out, :630/:642) but is
 * still a first-class member of the blob — getCollections auto-picks it
 * (js:667-672) and buildParamsBlob below persists it — so it resets with the
 * rest.
 *
 * Legacy's `qty_id` (:335), `assembly_configuration_id` (:554) and `housing_id`
 * (:890) also carry the class but have no counterpart in this state object
 * (dead / placeholder-grade selects — see the header note), and
 * `#burner_assy_config` (:607) is commented out in the template, so the live
 * `assy_config_id` is read from the MODEL half (js:759-764) and is preserved.
 */
const GH_PARAM_KEYS = [
  'mfg_id',
  'type_id',
  'series_id',
  'power_id',
  'weather_rating_id',
  'certification_id',
  'venting_id',
  'conn_hand_id',
  'heatex_mtl_id',
  'tube_rows_vertically',
  'configuration',
  'heatex_qty_id',
  'burner_mtl_id',
  'fuel_type_id',
  'firing_mode_id',
  'pilot_id',
  'burner_config_id',
  'qty_wide',
  'qty_high',
  'output_rating_std',
  'blank_off_id',
] as const;

/**
 * Selector-parity — `params_gas_heater.input_rating_std`
 * (gas_heater.selector.js:1988-1989). Legacy re-derives it from the DOM on
 * EVERY save: it finds the Available Heater Sizes option whose value is the
 * current `output_rating_std`, takes its label — which is
 * `"<input_rating_std>/<output_rating_std>"` (calcRating, js:1431-1440) — and
 * strips the `/<output>` tail back off. The port never wrote the key, so the
 * board form's read-only "Input Rating" (component/GasHeaterForm.tsx) came
 * back blank after a React Select: it reads this blob key directly.
 *
 * Derived from the same option rows the select is built from rather than from
 * the rendered DOM, which is the same value by construction and survives the
 * dialog unmounting.
 */
export function ghInputRatingStd(
  rows: Array<{ id?: unknown; name?: unknown }>,
  outputRatingStd: unknown
): string | null {
  if (outputRatingStd == null || outputRatingStd === '') return null;
  const hit = rows.find((r) => String(r.id) === String(outputRatingStd));
  if (!hit || hit.name == null) return null;
  return String(hit.name).replace(`/${outputRatingStd}`, '');
}

const num = (v: string): number | null => {
  if (v === '' || v === 'None' || v === 'none') return null;
  const n = Number(v.replace(/,/g, ''));
  return Number.isNaN(n) ? null : n;
};

/** Capacity Required total (gas_heater.selector.js:1300 / :1340) —
 * `Math.round(airflow * (ladb - eadb) * 1.085)`, computed client-side and
 * painted into the read-only field on every render (js:1139-1141). Null unless
 * all three inputs are present, which is legacy's guard too (it blanks the
 * field and empties the heater-size list in that case). */
const ghCapacityRequiredTotal = (
  airflow: unknown,
  eadb: unknown,
  ladb: unknown
): number | null => {
  // legacy decodes the formatted field before arithmetic (bindFormatter's
  // `.replace(/,/g,'')`, mirrored on the qty selects at js:1284/1291), so a
  // committed "6,000" must not fall through to NaN.
  const dec = (v: unknown) =>
    typeof v === 'string' ? Number(v.replace(/,/g, '')) : Number(v);
  const a = dec(airflow);
  const e = dec(eadb);
  const l = dec(ladb);
  if (airflow == null || eadb == null || ladb == null) return null;
  if (airflow === '' || eadb === '' || ladb === '') return null;
  if (Number.isNaN(a) || Number.isNaN(e) || Number.isNaN(l)) return null;
  return Math.round(a * (l - e) * 1.085);
};

/** `data-min="40" data-max="200"` on the Desired Leaving Air Temp input and
 * `data-max="195"` on the Entering Air Temperature one
 * (gas_heater.selector.tmpl:763/777). Legacy reads them generically in
 * changeModelField (`+e.target.getAttribute('data-min')`, js:1639-1643) and
 * clamps the committed value into range. */
const GH_EADB_TEMP_MAX = 195;
const GH_LADB_TEMP_DESIRED_MIN = 40;
const GH_LADB_TEMP_DESIRED_MAX = 200;

/** The desired leaving air temperature must sit at least this far above the
 * entering one (gas_heater.selector.js:1654-1660). */
const GH_TEMP_MIN_RISE = 5;

/** The generic data-min/data-max clamp changeModelField applies to the changed
 * input (js:1639-1643), keyed by field name. Manifold Pressure's bounds depend
 * on `fuel_type_id` rather than being a fixed attribute, so it is resolved
 * through the same helper the template uses. Legacy's `if (min && …)` treats a
 * missing attribute (NaN) and 0 alike, so an absent bound is simply left out. */
const ghInputRange = (
  name: string,
  current: Record<string, unknown>
): { min?: number; max?: number } => {
  if (name === 'eadb_temp') return { max: GH_EADB_TEMP_MAX };
  if (name === 'ladb_temp_desired') {
    return { min: GH_LADB_TEMP_DESIRED_MIN, max: GH_LADB_TEMP_DESIRED_MAX };
  }
  if (name === 'burner_manifold_pressure_conditional') {
    return ghManifoldPressureRange(current.fuel_type_id);
  }
  return {};
};

/** Apply that clamp to a decoded number. */
const ghClamp = (value: number, { min, max }: { min?: number; max?: number }) =>
  min != null && value < min ? min : max != null && value > max ? max : value;

/**
 * EADB / desired-LADB interlock, ported from changeModelField
 * (gas_heater.selector.js:1637-1659).
 *
 * Legacy keeps the desired leaving air temperature at least GH_TEMP_MIN_RISE
 * degrees above the entering air temperature, and it enforces that by
 * REWRITING the leaving temperature rather than rejecting the edit:
 *
 *  - editing EADB to within 5 degrees of the current LADB pushes LADB to
 *    `EADB + 5` (js:1651-1653) — but only when LADB is already filled in; a
 *    blank LADB is left blank.
 *  - editing LADB to less than 5 degrees above EADB snaps it to `EADB + 5`
 *    (js:1654-1660).
 *
 * Both writes go through jQuery `.val(...).change()`, which re-enters
 * changeModelField and so re-applies the data-min/data-max clamp — hence the
 * `ghClamp(…)` on the rewritten value.
 *
 * Arithmetic runs on the formatter's DECODED number (formatter.js:229
 * `getValue()` → `decode()` → `parseFloat`), not the displayed string.
 *
 * Returns the fields to merge over the caller's own `{[name]: raw}` write.
 */
const ghTempInterlock = (
  name: string,
  raw: string | null,
  current: Record<string, unknown>
): Record<string, string> => {
  if (name === 'eadb_temp') {
    const eadb = raw == null ? null : num(raw);
    const ladbRaw = current.ladb_temp_desired;
    // legacy guard: `$('#ladb_temp_desired_id input').val() !== ''`
    if (eadb == null || ladbRaw == null || ladbRaw === '') return {};
    const ladb = num(String(ladbRaw)) ?? 0;
    if (ladb - eadb >= GH_TEMP_MIN_RISE) return {};
    const ladbRange = ghInputRange('ladb_temp_desired', current);
    let next = ghClamp(eadb + GH_TEMP_MIN_RISE, ladbRange);
    // Legacy quirk, reproduced deliberately. The `.change()` re-entry runs
    // BEFORE `self.model_data.model[name] = value` (js:1697), so the nested
    // LADB branch compares against the PREVIOUS entering temperature, not the
    // one just typed. It only shows when the form starts out already violating
    // the rule (saved EADB less than 5 below the current LADB) — lowering EADB
    // from 100 to 55 against a LADB of 20 leaves 105 on dev, not 60.
    const prevEadb = Number(current.eadb_temp);
    if (next - prevEadb < GH_TEMP_MIN_RISE) {
      next = ghClamp(prevEadb + GH_TEMP_MIN_RISE, ladbRange);
    }
    return { ladb_temp_desired: String(next) };
  }

  if (name === 'ladb_temp_desired') {
    const decoded = raw == null ? null : num(raw);
    if (decoded == null) return {};
    const range = ghInputRange(name, current);
    let ladb = ghClamp(decoded, range);
    // legacy: `const eadb_temp = +self.model_data.model.eadb_temp` — a missing
    // EADB coerces to NaN, and `ladb - NaN < 5` is false, so the interlock is a
    // no-op until an entering temperature exists.
    const eadb = Number(current.eadb_temp);
    if (ladb - eadb < GH_TEMP_MIN_RISE) {
      ladb = ghClamp(eadb + GH_TEMP_MIN_RISE, range);
    }
    return ladb === decoded ? {} : { ladb_temp_desired: String(ladb) };
  }

  return {};
};

/** Manifold Pressure (`burner_manifold_pressure_conditional`) data-min/
 * data-max/data-step, conditional on `fuel_type_id`
 * (gas_heater.selector.tmpl:521): `fuel_type_id === 1` (NG) is
 * [4.20, 8.00]; anything else (LP) is [1.60, 3.00]. Step is 0.01 either way.
 * changeModelField (js:1639-1643) reads data-min/data-max generically off
 * the changed input and clamps the committed value into range. */
const ghManifoldPressureRange = (fuelTypeId: unknown) =>
  Number(fuelTypeId) === 1
    ? { min: 4.2, max: 8.0 }
    : { min: 1.6, max: 3.0 };

/** Same range, keyed for a direct spread into `numInput`'s dataMin/dataMax/
 * dataStep props. */
const ghManifoldPressureInputProps = (fuelTypeId: unknown) => {
  const { min, max } = ghManifoldPressureRange(fuelTypeId);
  return { dataMin: min, dataMax: max, dataStep: 0.01 };
};

/** Manifold Pressure default, re-stamped whenever `fuel_type_id` changes
 * (changeParams, js:2212-2216): 1.6 for `fuel_type_id === 2` (LP), 4.4
 * otherwise (NG). Fires on every fuel-type change, not just the initial
 * load — it overwrites whatever the field already held. */
const ghManifoldPressureDefault = (fuelTypeId: unknown) =>
  Number(fuelTypeId) === 2 ? 1.6 : 4.4;

/** parse the saved gas_heater_params JSON blob (legacy obj). */
function parseParams(raw: unknown): Record<string, unknown> {
  if (raw == null) return {};
  try {
    return JSON.parse(String(raw)) as Record<string, unknown>;
  } catch {
    return {};
  }
}

export const GasHeaterSelector = ({
  modelId,
  ahu,
  activate,
  setSelectEnabled,
  setPrintEnabled,
}: SelectorTabProps) => {
  // ---- read-only loads (fire on mount; never mutate) ----
  const {
    data: detail,
    isLoading: detailLoading,
    isError: detailError,
    fulfilledTimeStamp: detailFulfilledAt,
  } = useGetComponentDetailQuery(
    { plural: 'gas_heaters', id: modelId },
    { refetchOnMountOrArgChange: true }
  );
  // the gas_heater CMS doc (legacy fn['current.gas_heater.cms']) — the static
  // selects (mfg/power/conn_hand/certification/blank_off) resolve through it.
  const { data: ghDoc, isLoading: ghDocLoading } =
    useGetComponentCmsDocQuery(GH_CMS_DOC_URL);
  // the GLOBAL cms doc (air.air_stream + weather_rating).
  const { data: globalDoc, isLoading: globalDocLoading } =
    useGetComponentCmsDocQuery(GLOBAL_CMS_URL);

  // static master collections (legacy fuel_type/firing_mode/voltages/etc.).
  const fuelTypeQ = useGetMasterListByUrlQuery(GH_COLLECTION_URLS.fuel_type);
  const firingModeQ = useGetMasterListByUrlQuery(GH_COLLECTION_URLS.firing_mode);
  const ventingQ = useGetMasterListByUrlQuery(GH_COLLECTION_URLS.venting);
  const voltagesQ = useGetMasterListByUrlQuery(GH_COLLECTION_URLS.voltages);
  const pricingQ = useGetMasterListByUrlQuery(GH_COLLECTION_URLS.pricing_methods);

  // ---- mutations (ALL userArmed-gated; never auto-fired — data-safety) ----
  const [rateGasHeater] = useRateGasHeaterMutation();
  const [requestPdf] = useRequestGasHeaterPdfMutation();

  // ---- saved params seed ----
  const savedParams = useMemo(
    () => parseParams((detail as IGasHeaterRow | undefined)?.gas_heater_params),
    [detail]
  );

  // ---- state model (legacy params_gas_heater + model fields) ----
  //
  // Declared HERE, above the param GETs, because the Available Heater Sizes
  // chain is keyed on the LIVE selection rather than the saved blob (see
  // `liveMfgId` below and `calcRating` parity further down).
  const state = useRef<Record<string, unknown>>({});
  const init = useRef<Record<string, unknown>>({});
  const seeded = useRef<number | null>(null);
  const [, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);

  // ---- param-chain GETs for the LIVE selection ----------------------------
  //
  // Every one of these keys on whatever Mfg / Type / Series is selected RIGHT
  // NOW, because that is what legacy does: `changeParams` writes the new value
  // onto `model_data.params_gas_heater` and immediately re-runs `getCollections`
  // (js:2193-2196), which re-fetches type → series → heatex_mtl → venting →
  // pilot_config → configuration → burner_mtl → burner_config → fuel_type from
  // the top.
  //
  // The port keyed them on the SAVED blob instead and left the live re-cascade
  // "deferred", so picking a different Manufacturer changed nothing but the
  // Manufacturer: Type and Series kept the old maker's values and every
  // dependent list kept the old maker's options. Switching Heatco (mfg 50,
  // Indirect Fire Tubular) to Midco International (mfg 188, Direct Fire) left
  // the dialog on type_id 1 / series 296, so the Heat Exchanger sub-tab stayed,
  // Burner Material and Pilot stayed empty and greyed (their lists are empty
  // for Heatco and populated for Midco), and the availability query went out as
  // `mfg_id=188&type_id=1&series_id=296&…` — a combination with no catalogue
  // rows at all. Compare dev, which on the same click issues
  // `params/type/?mfg_id=188` → `qty/type/3` → `params/series/?mfg_id=188&
  // type_id=3` → pilot_config / burner_mtl / burner_config for 188/3/1068.
  //
  // Before the seed effect runs `state.current` is `{}`, so these fall back to
  // the saved ids and the first render is unchanged.
  const p = savedParams;
  const mfgId = state.current.mfg_id ?? p.mfg_id ?? GH_DEFAULTS.mfg_id;
  const typeId = state.current.type_id ?? p.type_id ?? GH_DEFAULTS.type_id;
  const seriesId = state.current.series_id ?? p.series_id ?? GH_DEFAULTS.series_id;
  const skip = (cond: boolean) => ({ skip: !cond });

  // aliases kept for the capacity chain below, which was already live-keyed.
  const liveMfgId = mfgId;
  const liveTypeId = typeId;
  const liveSeriesId = seriesId;

  const typeListQ = useGetMasterListByUrlQuery(
    ghParamUrls.type(mfgId as number),
    skip(mfgId != null)
  );
  const seriesListQ = useGetMasterListByUrlQuery(
    ghParamUrls.series(mfgId as number, typeId as number),
    skip(mfgId != null && typeId != null)
  );
  const heatexMtlQ = useGetMasterListByUrlQuery(
    ghParamUrls.heatex_mtl(mfgId as number, typeId as number, seriesId as number),
    skip(seriesId != null)
  );
  const configurationQ = useGetMasterListByUrlQuery(
    ghParamUrls.configuration(mfgId as number, typeId as number, seriesId as number),
    skip(seriesId != null)
  );
  const ventingParamQ = useGetMasterListByUrlQuery(
    ghParamUrls.venting(mfgId as number, typeId as number, seriesId as number),
    skip(seriesId != null)
  );
  const pilotQ = useGetMasterListByUrlQuery(
    ghParamUrls.pilot_config(mfgId as number, typeId as number, seriesId as number),
    skip(seriesId != null)
  );
  // Legacy gates this fetch on the heater NOT being direct fire
  // (`if (self.types[0].value != "direct_fire")`, js:547); direct fire is
  // type_id 3 (`ksdm_master_ahu_gas_heater_type`, and legacy's own isDirectFire
  // at js:1205). The port skipped on type_id 1 instead — Indirect Fire Tubular,
  // i.e. exactly the type whose catalogue (`..._it_hm.heatex_qty`) is the only
  // one that HAS quantities — so every Heatco/indirect-tubular heater rendered
  // the Qty select with nothing but the hardcoded "- -".
  const heatexQtyQ = useGetMasterListByUrlQuery(
    ghParamUrls.heatex_qty(mfgId as number, typeId as number, seriesId as number),
    skip(seriesId != null && Number(typeId) !== 3)
  );
  // Tube Rows + Firing Mode are the two lists calcRating re-fetches whenever
  // the heater size changes (js:1460 / :1477), scoped to that size — so they
  // key on the LIVE output_rating_std, not the saved one.
  const liveOutputRatingStd =
    (state.current.output_rating_std as number | null | undefined) ??
    (p.output_rating_std as number | undefined);
  const tubeRowsQ = useGetMasterListByUrlQuery(
    ghParamUrls.tube_rows_vertically(
      liveMfgId as number,
      liveTypeId as number,
      liveSeriesId as number,
      (liveOutputRatingStd as number) ?? ''
    ),
    skip(liveSeriesId != null && liveOutputRatingStd != null)
  );
  const firingModeParamQ = useGetMasterListByUrlQuery(
    ghParamUrls.firing_mode(
      liveMfgId as number,
      liveTypeId as number,
      liveSeriesId as number,
      (liveOutputRatingStd as number) ?? ''
    ),
    skip(liveSeriesId != null)
  );

  // Burner sub-tab Material list (legacy collections.burnerMTL). The port had
  // no loader for it because the field wasn't rendered at all.
  const burnerMtlQ = useGetMasterListByUrlQuery(
    ghParamUrls.burner_mtl(mfgId as number, typeId as number, seriesId as number),
    skip(seriesId != null)
  );
  // Burner sub-tab Burner list (legacy collections.burnerConfig, getBurnerConfig
  // js:451-453). Scoped by the chosen burner material + certification, and
  // fetched by getCollections for every non-type-1 heater (js:664).
  const burnerConfigQ = useGetMasterListByUrlQuery(
    ghParamUrls.burner_config(
      mfgId as number,
      typeId as number,
      seriesId as number,
      state.current.burner_mtl_id as number,
      state.current.certification_id as number
    ),
    skip(
      seriesId != null &&
        state.current.burner_mtl_id != null &&
        state.current.certification_id != null
    )
  );
  // qty_wide / qty_high ranges (legacy getQty, js:455-481 — the endpoint
  // returns one row of {max_qty_wide, max_qty_high} and legacy expands each
  // into 1..max). The port left both selects on an empty list.

  const qtyQ = useGetComponentCmsDocQuery(
    ghParamUrls.qty(typeId as number),
    skip(typeId != null)
  );
  // "Available Heater Sizes" = the capacity model (legacy capacityModel +
  // calcRating). capacity_required is the PER-HEATER share of the required
  // capacity — total / (qty_wide * qty_high), rounded (js:1341-1347) — and
  // mfg 188 skips the fetch entirely (js:1426).
  //
  // All four inputs are read LIVE. Legacy recomputes them inside
  // calcCapacityRequiredTotal, which is bound to `change .model_field` and
  // `change .capacity_required_total_change` (js:99/122) — i.e. Airflow, EADB
  // and Desired LADB — and reads the Qty Wide/High SELECTS, not the saved
  // params (js:1281-1293). Typing a new LADB on dev therefore reloads the size
  // list; on dev3 it did not, because both the capacity and the parent ids
  // came from the saved model.
  const capacityTotal = ghCapacityRequiredTotal(
    state.current.airflow_actual,
    state.current.eadb_temp,
    state.current.ladb_temp_desired
  );
  const capacityPerHeater =
    capacityTotal == null
      ? null
      : Math.round(
          capacityTotal /
            (Number(state.current.qty_wide || 1) *
              Number(state.current.qty_high || 1))
        );
  // legacy's guard: with any of airflow/EADB/LADB blank it empties the size
  // select outright (`$('#input_rating_std').empty(); self.capacity = []`,
  // js:1324-1326) rather than showing a stale list.
  const capacityEnabled =
    liveSeriesId != null &&
    capacityPerHeater != null &&
    Number(liveMfgId) !== GH_MFG_188;
  const outputRatingStdQ = useGetMasterListByUrlQuery(
    ghParamUrls.output_rating_std(
      liveMfgId as number,
      liveTypeId as number,
      liveSeriesId as number,
      capacityPerHeater ?? ''
    ),
    skip(capacityEnabled)
  );

  /* Which of the availability-count fields currently render a select with NO
   * options. collectData reads `elem.val()` off the DOM, so on dev an empty
   * select simply contributes nothing to the filter string; this component
   * keeps the last value it was handed, so the omission has to be explicit.
   * See ghCountFilters' `emptyFields`.
   *
   * Only lists that come from a param GET can be empty — the doc-resolved and
   * static-master ones always have rows — and only once the GET has actually
   * resolved, so an in-flight list never transiently widens the count. */
  const resolvedEmpty = (q: { data?: unknown }) =>
    Array.isArray(q.data) && q.data.length === 0;
  const ghEmptyFields = useMemo(() => {
    const out = new Set<string>();
    if (resolvedEmpty(typeListQ)) out.add('type_id');
    if (resolvedEmpty(seriesListQ)) out.add('series_id');
    if (resolvedEmpty(ventingParamQ)) out.add('venting_id');
    if (resolvedEmpty(heatexMtlQ)) out.add('heatex_mtl_id');
    if (resolvedEmpty(configurationQ)) out.add('configuration');
    if (resolvedEmpty(burnerMtlQ)) out.add('burner_mtl_id');
    if (resolvedEmpty(tubeRowsQ)) out.add('tube_rows_vertically');
    // Firing Mode falls back to the static master collection for direct fire
    // (js:1368), so only the non-direct branch can be empty.
    if (Number(typeId) !== 3 && resolvedEmpty(firingModeParamQ)) out.add('firing_mode_id');
    return out;
  }, [
    typeListQ.data,
    seriesListQ.data,
    ventingParamQ.data,
    heatexMtlQ.data,
    configurationQ.data,
    burnerMtlQ.data,
    tubeRowsQ.data,
    firingModeParamQ.data,
    typeId,
  ]);

  // catalog list rows (param GETs) — typed loosely. Hoisted above the write
  // callbacks: the direct-fire burner builder needs both this and isDirectFire.
  const rowsOf = (q: { data?: unknown }): OptionRow[] =>
    (q.data as OptionRow[] | undefined) ?? [];

  // type-3 = direct fire: legacy drops the Options + Bypass input tabs and the
  // Heat Exchanger sub-tab (`{{#unless isDirectFire}}`, tmpl:17-23 / 111-115)
  // and, conversely, SHOWS the burner-assembly column (showBurnerAssy,
  // js:1387-1394). Read off the live-or-saved type id rather than
  // `state.current`, so it is already right on the first render.
  const isDirectFire = Number(typeId) === 3;

  const catalogsLoading =
    fuelTypeQ.isLoading ||
    firingModeQ.isLoading ||
    ventingQ.isLoading ||
    voltagesQ.isLoading ||
    pricingQ.isLoading ||
    typeListQ.isLoading ||
    seriesListQ.isLoading;

  // userArmed gate (arms ONLY on a real #rate/#select/#print click).
  const userArmed = useRef(false);
  const saveAbort = useRef<{ abort: () => void } | null>(null);
  const dispatch = useAppDispatch();

  // ---- ★ seed only from a row that POSTDATES this open (the reopen gate) ----
  // RTK Query keeps `getComponentDetail`'s unsubscribed entry for its default
  // 60 s (its only tag is invalidated by the cascade refresh, nothing else),
  // and the chassis mounts a fresh selector on every
  // open (`key={dialog.openSeq}`, ComponentSelectorDialog.tsx). So a reopen
  // inside that minute is served the CACHED row synchronously:
  // `refetchOnMountOrArgChange` does re-pull, but `isLoading` is false the whole
  // time (it is true only on the very first ever load) and the seed below
  // latches `seeded.current = modelId`, which blocks any re-seed when the fresh
  // row lands. Reopening straight after a Select therefore seeded the PRE-select
  // row — and since the output sheet is gated on `master_gas_heater_id || model`
  // (`hasRating`), a heater selected for the first time came back up with
  // `ratedCount` 0 and an EMPTY output sheet, correct again only after a page
  // refresh. That is the reported bug.
  //
  // ⚠ `isFetching` is not a usable gate: RTK Query starts the refetch from an
  // effect inside the hook, so the first render after mount still reports
  // `isFetching: false` next to the stale `data` — the seed effect runs in that
  // very commit. `fulfilledTimeStamp` is stamped when a request FULFILS, so a
  // row cached before this dialog opened can never satisfy `>= openedAt`.
  // (Same gate, same reasoning as CoilSelector.tsx and EvapPackSelector.tsx,
  // both measured on dev3.)
  const openedAt = useRef(Date.now());
  // …and a GET issued while the previous Select is still saving would fulfil
  // AFTER `openedAt` while still returning the pre-select row: doSelect emits
  // `rating_mode:hide` on the click, before its `/select` POST resolves (dev
  // eed97f68ac), and `master_gas_heater_id` is not persisted until that POST
  // lands. So when this open follows a Select whose chain is in flight, hold the
  // gate open (`Infinity`) until those writes settle, then re-read and gate on
  // THAT moment. Legacy has no equivalent hole — Backbone `model.save(attrs)`
  // sets `attrs` in memory on the way past, so its reopened dialog reads the
  // just-selected values regardless of the XHR.
  const pendingWrites = useRef(pendingSelectWrites(selectWriteKey('gas_heaters', modelId)));
  const [gateAt, setGateAt] = useState<number>(() =>
    pendingWrites.current ? Number.POSITIVE_INFINITY : openedAt.current
  );
  useEffect(() => {
    const writes = pendingWrites.current;
    if (!writes) return;
    let alive = true;
    void writes.then(() => {
      if (!alive) return;
      // stamped BEFORE the refetch is dispatched, so only its (later)
      // fulfilment can satisfy the gate.
      const at = Date.now();
      void dispatch(
        ahuComponentFormsApi.endpoints.getComponentDetail.initiate(
          { plural: 'gas_heaters', id: modelId },
          { forceRefetch: true, subscribe: false }
        )
      );
      setGateAt(at);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId]);
  // `isError` is the escape hatch: a failed read seeds from whatever is cached
  // rather than pinning the preloader on for ever.
  const detailFresh = detailError || (detailFulfilledAt != null && detailFulfilledAt >= gateAt);

  // Dev-sync J6 — the STAGED section season (gas_heater.selector.js:1627-1634
  // changeSeasonField: season_id belongs on the SECTION, and the live section
  // model autosaves on .set(), so legacy stages the pick on its detached
  // model_data.section snapshot; ONLY doSelect persists it (:1015-1020)).
  const seasonIdRef = useRef<number | null>(null);

  // active input tab + the Heater tab's active sub-tab.
  const [activeTab, setActiveTab] = useState<TabName>('Heater');
  const [activeSubTab, setActiveSubTab] = useState<SubTabName>('properties_more');

  // the rated/stored model echoed by the GET (or by a fresh Rate). Read-only it
  // is the saved model JSON (the legacy render_outputs initial path). The pager
  // index when multiple results.
  const [rated, setRated] = useState<IGasHeaterRow | null>(null);
  const [ratedIndex, setRatedIndex] = useState(0);
  const ratedDatetime = useRef<string | null>(null);

  // ---- the CATALOG page behind the output sheet (legacy gas_heaters_list) ----
  //
  // render_outputs(rated_model, count, index) takes THREE things, and the port
  // supplied none of them properly: it passed the AHU model row as
  // `rated_model` and hard-coded count 1. On dev `rated_model` is a row from
  // `loadParams()` — GET cms/…/ahu/gas_heaters/all/?<the same filter string the
  // count badge uses> — i.e. the MASTER catalog row that was rated. That row is
  // where the tubular sheet's Model / Number of Heat-Exchangers / Gas Inlet
  // Hand / Gas Inlet NPT Size come from, so with the AHU row in its place those
  // four printed blank on a perfectly well rated heater. `count` is the number
  // of catalog matches and drives the result pager.
  //
  // It is also what makes Rate work at all for type 1: legacy saves
  // `master_gas_heater_id: rated_models[index]?.id` (js:2074/2095), so the
  // catalog row IS the selection. The port sent back whatever master id was
  // already stored — null on a never-rated heater — so a tubular Rate could
  // never pick a heater and every computed output (Capacity, Face Velocity,
  // APD, LADB …) stayed empty.
  const [catalog, setCatalog] = useState<IGasHeaterRow[]>([]);
  /** the `rated_model` argument — the catalog row, once resolved. */
  const [ratedRow, setRatedRow] = useState<IGasHeaterRow | null>(null);
  /** the `count` argument (1 on the initial render, list length after a Rate). */
  const [ratedCount, setRatedCount] = useState(0);
  const [triggerCatalog] = useLazyGetGasHeaterCatalogQuery();

  // ---- seed state + init once GET resolves ----
  useEffect(() => {
    if (!detail) return;
    if (seeded.current === modelId) return;
    // ★ the reopen gate (see `gateAt` above). The seed LATCHES, so it must not
    // run against a row that predates this open — on a reopen straight after a
    // Select that row is the pre-select one and the output sheet comes up empty.
    if (!detailFresh) return;
    const m = detail as IGasHeaterRow;
    const params = parseParams(m.gas_heater_params);
    state.current = {
      // the param-chain selection (legacy model_data.params_gas_heater)
      mfg_id: params.mfg_id ?? GH_DEFAULTS.mfg_id,
      type_id: params.type_id ?? GH_DEFAULTS.type_id,
      series_id: params.series_id ?? GH_DEFAULTS.series_id,
      heatex_mtl_id: params.heatex_mtl_id,
      tube_rows_vertically: params.tube_rows_vertically,
      configuration: params.configuration,
      heatex_qty_id: params.heatex_qty_id,
      venting_id: params.venting_id ?? m.venting_id,
      pilot_id: params.pilot_config_id ?? params.pilot_id ?? m.gas_pilot_config_id,
      fuel_type_id: params.fuel_type_id ?? m.fuel_type_id,
      firing_mode_id: params.firing_mode_id ?? m.firing_mode_id,
      burner_mtl_id: params.burner_mtl_id,
      burner_config_id: params.burner_config_id,
      certification_id: params.certification_id,
      power_id: params.power_id ?? m.power_id,
      conn_hand_id: params.conn_hand_id ?? m.conn_hand_id,
      weather_rating_id: params.weather_rating_id ?? m.weather_rating_id,
      output_rating_std: params.output_rating_std ?? params.output_rating_std_total,
      qty_wide: params.qty_wide ?? m.qty_wide ?? 1,
      qty_high: params.qty_high ?? m.qty_high ?? 1,
      // the performance/model fields (legacy model_data.model)
      airflow_actual: m.airflow_actual,
      air_stream_id: m.air_stream_id,
      eadb_temp: m.eadb_temp,
      ladb_temp_desired: m.ladb_temp_desired,
      gas_inlet_pressure: m.gas_inlet_pressure,
      burner_manifold_pressure_conditional: m.burner_manifold_pressure_conditional,
      assy_height_from_floor: m.assy_height_from_floor ?? params.assy_height_from_floor ?? 0,
      assy_distance_from_left_wall: m.assy_distance_from_left_wall ?? 0,
      assy_config_id: m.assy_config_id,
      blank_off_id: m.blank_off_selection_id,
      pricing_method_id: m.pricing_method_id,
      cost_product_user: m.cost_product_user,
      cost_freight_user: m.cost_freight_user,
    };
    // Dev-sync J5 — the chart's pending re-rate handoff (gas_heater.
    // selector.js:216-326): section-scoped gate; the fresh ladb/lawb become
    // the DESIRED pair too (:307-308 — legacy assigns unconditionally, so an
    // absent source key OVERWRITES the desired value with undefined; quirk
    // preserved); fields land on the merged model+section seed (:309-310);
    // changed=true + the Performance tab (:317/:325).
    const sec = activate.section as { id?: number; season_id?: number | null } | undefined;
    // J6 — seed the staged season from the section snapshot (legacy
    // model_data.section starts as the section's toJSON()).
    seasonIdRef.current = (sec?.season_id as number | null | undefined) ?? null;
    const pendingFields = takeSectionPendingRerate(sec?.id);
    if (pendingFields) {
      const f = pendingFields as Record<string, number | undefined>;
      f.ladb_temp_desired = f.ladb_temp;
      f.lawb_temp_desired = f.lawb_temp;
      Object.assign(state.current, f);
      setActiveTab('Performance');
    }
    init.current = { ...state.current };
    seeded.current = modelId;
    // render the STORED rated state (legacy withoutRatingSeparateAPI /
    // render_outputs initial path) — read-only, zero writes.
    //
    // ONLY when the heater actually carries a rating. Legacy gates both of its
    // initial render_outputs calls on that (js:1206/1227):
    //   if  (!changed && (master_gas_heater_id || isDirectFire && temporary != undefined))
    //   else if (!changed && current_model.model && temporary != undefined)
    // where isDirectFire itself requires `current_model.model` (:1205) and
    // `temporary` is seeded to that same model string (:367) — so both branches
    // reduce to `master_gas_heater_id || model`, and an unrated heater leaves
    // #for-output-rendering EMPTY on dev. The port seeded unconditionally, so a
    // heater with neither drew the output sheet anyway — and, with no saved
    // type_id in gas_heater_params, `model.type` is null (get_type reads
    // params, models_component_gas_heater.py:282), so `type.value` !=
    // 'id_tubular' and it drew the DIRECT-FIRE sheet, fully blank, under an
    // "Indirect Fire Tubular" input pane.
    const hasRating = Boolean(m.master_gas_heater_id) || Boolean(m.model);
    setRated(hasRating ? m : null);
    ratedDatetime.current = (m.rating_datetime as string) ?? null;
    // …and resolve the `rated_model` the sheet is drawn from. Legacy's first
    // branch walks the catalog page for the row whose id is the stored
    // master_gas_heater_id (direct fire takes the FIRST row instead, js:1211)
    // and renders it with count 1 / index 0; when the row is not in the page it
    // falls back to a merged {model, gas_heater_params, conn_hand_name} object
    // (js:1216-1225). The else-if branch (a `model` with no master id) passes
    // the model itself, which the same fallback covers.
    setRatedRow(null);
    setRatedCount(hasRating ? 1 : 0);
    setRatedIndex(0);
    // isDirectFire (js:1205) — a direct-fire heater takes the FIRST catalog row
    // instead of matching on the master id (it has none).
    const isDirectFire =
      Boolean(m.model) && Number((m.type as { id?: number } | null | undefined)?.id) === 3;
    if (hasRating && (Boolean(m.master_gas_heater_id) || isDirectFire)) {
      const elev =
        (state.current.elevation as number | undefined) ??
        ((sec as { elevation?: number } | undefined)?.elevation as number | undefined) ??
        0;
      triggerCatalog(ghCatalogUrl(ghCountFilters(state.current, elev, ghEmptyFields)), true)
        .unwrap()
        .then((list) => {
          setCatalog(list);
          const row = list.find(
            (it, idx) =>
              String((it as { id?: unknown }).id) === String(m.master_gas_heater_id) ||
              (idx === 0 && isDirectFire)
          );
          if (row) setRatedRow(row);
          bump();
        })
        .catch(() => {
          /* the merged fallback below covers a failed/empty catalog page. */
        });
    }
    bump();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, detailFresh, modelId, bump, triggerCatalog]);

  /**
   * `rated_model` — the catalog row when we have it, else legacy's `!exist`
   * fallback: the model merged with its gas_heater_params plus the
   * connection-hand NAME resolved from the CMS doc (js:1217-1224). Derived
   * rather than stored so it still fills in once the CMS doc arrives.
   */
  const ratedModel = useMemo(() => {
    if (ratedRow) return ratedRow;
    if (!rated) return null;
    const params = parseParams(rated.gas_heater_params);
    return {
      ...rated,
      ...params,
      conn_hand_name: ghCmsSelectedLabel(
        ghDoc as GasHeaterCmsDoc,
        globalDoc as GasHeaterCmsDoc,
        'conn_hand_id',
        (params.conn_hand_id ?? rated.conn_hand_id) as number | undefined
      ),
    } as IGasHeaterRow;
  }, [ratedRow, rated, ghDoc, globalDoc]);

  // mfg 188 hides the count badge + available-sizes + heat-exchanger subtab.
  const isMfg188 = Number(state.current.mfg_id) === GH_MFG_188;

  // ---- availability COUNT badge (legacy `<span id="qty">{{count}}</span> /
  //      <span id="all_count">{{all_count}}</span>`, tmpl:33-35).
  //
  //      The badge markup, the RTKQ endpoints and the URL builders were all
  //      ported, but nothing ever CONNECTED them: both spans rendered as empty
  //      elements, so the badge showed a bare "/" with no numbers. Wire them.
  //
  //      `count`     = collectData() — the filtered catalog count, refetched on
  //                    every `.search_field` select change (js:1189-1197).
  //      `all_count` = getAllCount() — the unfiltered total, fetched ONCE per
  //                    session (js:239-248 caches it in stateStorage; RTKQ's
  //                    24h keepUnusedDataFor + the constant URL key is the same
  //                    thing, so this is one request per page load).
  //
  //      No debounce: legacy's `setTimeout(fn)` has NO delay argument (js:1189)
  //      — it only coalesces within a tick — and the count is driven by SELECT
  //      changes, never by typing. Legacy skips the refetch outright when a
  //      text field changed (`modelFieldChanged`, js:1185-1187), which is why
  //      onModelField must NOT feed this. ----
  const countElevation =
    (state.current.elevation as number | undefined) ??
    ((activate.section as Record<string, unknown> | undefined)?.elevation as
      | number
      | undefined) ??
    0;
  const { data: ghCount } = useGetGasHeaterCountQuery(
    ghCountUrl(ghCountFilters(state.current, countElevation, ghEmptyFields))
  );
  const { data: ghAllCount } = useGetGasHeaterCountQuery(GH_ALL_COUNT_URL);

  // checkRequiredFields → #rate gate (legacy checkAndUpdateRequiredFields).
  // Legacy ANDs in `!!self.count` (js:1813) — a zero-availability filter set
  // disables Rate even when every required field is filled.
  const requiredFilled = useMemo(() => {
    const s = state.current;
    const req = GH_REQUIRED_FIELDS[Number(s.mfg_id)] ?? GH_REQUIRED_FIELDS[50];
    for (const name of req) {
      const v = s[name];
      if (v == null || v === '' || v === 'None') return false;
    }
    return Boolean(ghCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.current, detail, ghCount]);

  // #select gate: enabled when the model carries a rating (legacy
  // checkButtonsAvailability removes #select.non-clickable when
  // master_gas_heater_id || model is set). Read-only: a saved/rated GH starts
  // with Select ENABLED (parity with legacy on a rated model); an unrated GH
  // (no master id, no model) keeps Select disabled until a Rate.
  // #print rides the SAME condition — legacy's checkButtonsAvailability
  // (gas_heater.selector.js:2583-2600) toggles `#print` and `#select` together
  // off `master_gas_heater_id || model`.
  useEffect(() => {
    const m = (rated ?? {}) as IGasHeaterRow;
    const hasRating = Boolean(m.master_gas_heater_id) || Boolean(m.model);
    setSelectEnabled(hasRating);
    setPrintEnabled?.(hasRating);
  }, [rated, setSelectEnabled, setPrintEnabled]);

  // #rate visual gate (legacy DOM surgery on #rate). A direct-fire heater
  // additionally stays disabled until the Burner sub-tab has been opened and
  // rated — `if (type_id == 3) { if (!burner_data_click) #rate.non-clickable }`
  // (js:1163-1167) — because the rate write for type 3 carries the burner
  // assembly, and there is none until then.
  useEffect(() => {
    const btn = document.getElementById('rate');
    if (!btn) return;
    const ok = requiredFilled && (!isDirectFire || burnerRated.current);
    btn.classList.toggle('non-clickable', !ok);
  });

  const markChanged = useCallback(() => {
    ahuBoardBus.emit('rating_mode:mounting:changed');
  }, []);

  // ---- field handlers ----
  const onParamField = useCallback(
    (name: string, raw: string) => {
      const value = name === 'configuration' || name === 'tube_rows_vertically'
        ? raw
        : name.endsWith('_id') ? num(raw) : raw;
      state.current = { ...state.current, [name]: value };
      // `if (name === 'mfg_id') params_gas_heater.tube_rows_vertically = null`
      // (js:2178) — the row count is per-maker, so it is dropped rather than
      // carried across, and the re-cascade below re-picks it.
      if (name === 'mfg_id') state.current.tube_rows_vertically = null;
      // changeParams (js:2206-2216): a fuel_type_id change re-stamps the
      // Manifold Pressure default, unconditionally overwriting whatever the
      // field already held — 1.6 for LP (fuel_type_id 2), 4.4 otherwise (NG).
      if (name === 'fuel_type_id') {
        state.current.burner_manifold_pressure_conditional = ghManifoldPressureDefault(value);
      }
      // `collectBurnerAssy` (js:862-895), bound to `change .get_burner_assy`
      // (js:118): re-reads the burner's own copy of the params and marks it
      // dirty. The port keeps one store, so only the flag is left to set.
      if (GH_BURNER_ASSY_DEPS.has(name)) burnerDirty.current = true;
      // a param change clears the prior rated outputs (legacy: empties
      // #for-output-rendering, nulls master_gas_heater_id/model).
      setRated(null);
      setRatedRow(null);
      setRatedCount(0);
      markChanged();
      bump();
    },
    [markChanged, bump]
  );

  const onModelField = useCallback(
    (name: string, raw: string | null) => {
      // changeModelField (js:1639-1643) clamps the committed value into the
      // input's data-min/data-max range FIRST, so the EADB/LADB interlock below
      // reasons about the clamped number — typing 400 into Entering Air
      // Temperature is a 195, and pushes Desired Leaving Air Temp to 200.
      let clamped = raw;
      if (clamped != null) {
        const decoded = num(clamped);
        if (decoded != null) {
          const next = ghClamp(decoded, ghInputRange(name, state.current));
          if (next !== decoded) clamped = String(next);
        }
      }
      const patch = ghTempInterlock(name, clamped, state.current);
      // An `_id` model field arrives off a <select> as a string; store it
      // numeric, as onParamField does — the Rate/Select payload builders read
      // `s.air_stream_id` straight through.
      const value = clamped == null ? null : name.endsWith('_id') ? num(clamped) : clamped;
      state.current = { ...state.current, [name]: value, ...patch };
      // `if (e.target.classList.contains('get_burner_assy_model')) { ...
      // burner_data_change = true }` (js:1704-1712), direct fire only. The
      // interlock's knock-on write to Desired LADB counts too — it is one of
      // those inputs.
      if (
        isDirectFire &&
        (GH_BURNER_ASSY_DEPS.has(name) || Object.keys(patch).some((k) => GH_BURNER_ASSY_DEPS.has(k)))
      ) {
        burnerDirty.current = true;
      }
      // an airflow/temp change invalidates the rated outputs (legacy
      // changeModelField nulls master + clears output) — except the
      // position/pressure fields that don't (air_stream/gas_inlet/assy_*).
      if (
        name !== 'air_stream_id' &&
        name !== 'gas_inlet_pressure' &&
        name !== 'assy_height_from_floor' &&
        name !== 'assy_distance_from_left_wall'
      ) {
        setRated(null);
        setRatedRow(null);
        setRatedCount(0);
      }
      markChanged();
      bump();
    },
    [markChanged, bump, isDirectFire]
  );

  /** the params_gas_heater blob persisted as a JSON string (legacy
   *  JSON.stringify(self.params_gas_heater)). Shared by the Rate/Select write
   *  and by the direct-fire burner rate, which sends the same blob (js:806). */
  // the Available Heater Sizes option rows, mirrored into a ref so
  // buildParamsBlob (deps `[]`, reads state.current) can resolve
  // input_rating_std without re-creating itself on every capacity refetch.
  const outputRatingRowsRef = useRef<Array<{ id?: unknown; name?: unknown }>>([]);

  const buildParamsBlob = useCallback((): Record<string, unknown> => {
    const s = state.current;
    return {
      mfg_id: s.mfg_id,
      type_id: s.type_id,
      series_id: s.series_id,
      heatex_mtl_id: s.heatex_mtl_id,
      tube_rows_vertically: s.tube_rows_vertically,
      configuration: s.configuration,
      heatex_qty_id: s.heatex_qty_id,
      venting_id: s.venting_id,
      conn_hand_id: s.conn_hand_id,
      certification_id: s.certification_id,
      power_id: s.power_id,
      weather_rating_id: s.weather_rating_id,
      fuel_type_id: s.fuel_type_id,
      firing_mode_id: s.firing_mode_id,
      pilot_config_id: s.pilot_id,
      burner_mtl_id: s.burner_mtl_id,
      burner_config_id: s.burner_config_id,
      qty_wide: s.qty_wide,
      qty_high: s.qty_high,
      output_rating_std: s.output_rating_std,
      output_rating_std_total: s.output_rating_std,
      // legacy stamps this alongside output_rating_std on every save
      // (gas_heater.selector.js:1988-1989); the board form's read-only
      // "Input Rating" reads it straight out of this blob.
      input_rating_std: ghInputRatingStd(outputRatingRowsRef.current, s.output_rating_std),
      assy_height_from_floor: s.assy_height_from_floor,
    };
  }, []);

  // ---- build the RATE/APPLY payload (searchGasHeaters model.save body) ----
  const buildPayload = useCallback(
    (isSelect: boolean, chosen?: IGasHeaterRow | null): GhRatePayload => {
      const s = state.current;
      const params = buildParamsBlob();
      const isType3 = Number(s.type_id) === 3;
      const m = (rated ?? detail ?? {}) as IGasHeaterRow;
      // legacy stamps the chosen catalog row's efficiency into the params blob
      // on the Select write only (js:2066).
      if (isSelect && chosen && (chosen as { efficiency?: unknown }).efficiency !== undefined) {
        params.efficiency = (chosen as { efficiency?: unknown }).efficiency;
      }
      // `master_gas_heater_id: rated_models[index]?.id` (js:2074/2095). An
      // empty catalog page leaves it undefined, which JSON.stringify drops
      // from the body — so the stored value survives, as on dev. Only fall
      // back to the stored id when no catalog row was resolved at all.
      const chosenMasterId =
        (chosen as { id?: unknown } | null | undefined)?.id ?? (m.master_gas_heater_id as number);
      return {
        gas_heater_params: JSON.stringify(params),
        eadb_temp: s.eadb_temp,
        airflow_actual: s.airflow_actual,
        air_stream_id: s.air_stream_id,
        gas_inlet_pressure: s.gas_inlet_pressure,
        qty_wide: isType3 ? 1 : s.qty_wide,
        qty_high: isType3 ? 1 : s.qty_high,
        power_id: s.power_id,
        conn_hand_id: s.conn_hand_id,
        venting_id: s.venting_id,
        weather_rating_id: s.weather_rating_id,
        burner_manifold_pressure_conditional: s.burner_manifold_pressure_conditional,
        ladb_temp_desired: s.ladb_temp_desired,
        // type-1 carries the chosen rated row's master id; type-3 carries the
        // assy_config_id (no master id).
        ...(isType3
          ? { assy_config_id: s.assy_config_id }
          : chosenMasterId != null
            ? { master_gas_heater_id: chosenMasterId as number }
            : {}),
        fuel_type_id: s.fuel_type_id,
        firing_mode_id: s.firing_mode_id,
        gas_pilot_config_id: s.pilot_id,
        assy_height_from_floor: s.assy_height_from_floor,
        assy_distance_from_left_wall: s.assy_distance_from_left_wall,
        rate: !isSelect, // RATE → rate:true; SELECT (re-search + persist) → rate:false
        isSelect: isSelect || undefined,
      };
    },
    [rated, detail, buildParamsBlob]
  );

  // ---- RATE (code-only, userArmed-gated) ----
  //
  // searchGasHeaters(rated_models, index) — the PATCH carries the catalog row
  // at `index` as the selection, and the response is drawn together with that
  // row and the full result count (js:2091-2120). Split out so the result
  // pager can re-run it at another index (legacy changeGasHeater, js:1981-1988,
  // which reuses the cached gas_heaters_list rather than re-fetching).
  const searchGasHeaters = useCallback(
    /** true = rated, false = failed, null = aborted (superseded / closed). */
    async (list: IGasHeaterRow[], index: number): Promise<boolean | null> => {
      const chosen = list[index] ?? null;
      try {
        const req = rateGasHeater({ id: modelId, data: buildPayload(false, chosen) });
        saveAbort.current = req as unknown as { abort: () => void };
        const result = await req.unwrap();
        setRated(result);
        setRatedRow(chosen);
        setRatedCount(list.length);
        setRatedIndex(index);
        ratedDatetime.current = (result.rating_datetime as string) ?? null;
        setSelectEnabled(true);
        bump();
        return true;
      } catch (err) {
        const e = err as { name?: string; error?: { name?: string } } | null;
        if ((e?.name ?? e?.error?.name) === 'AbortError') return null;
        // eslint-disable-next-line no-console
        console.error('[gas_heater selector] rate failed', err);
        return false;
      }
    },
    [rateGasHeater, modelId, buildPayload, setSelectEnabled, bump]
  );

  const doRate = useCallback(async () => {
    if (!requiredFilled) return;
    userArmed.current = true;
    saveAbort.current?.abort?.();
    // legacy rate() loads the catalog page FIRST and rates against its first
    // row (js:1934-1940 → searchGasHeaters(value, 0)).
    const startedAt = Date.now();
    const url = ghCatalogUrl(ghCountFilters(state.current, countElevation, ghEmptyFields));
    let list: IGasHeaterRow[] = [];
    try {
      list = await triggerCatalog(url, true).unwrap();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[gas_heater selector] catalog page failed', err);
    }
    setCatalog(list);
    const ok = await searchGasHeaters(list, 0);
    // Counted here, not in searchGasHeaters, so paging through results is not
    // a new rating; an aborted one (re-rate, close) is not counted at all.
    // Board-only selector; ratings come from the catalog.
    if (ok === null) return;
    track('component_rated', {
      component: 'gas_heater',
      manufacturer: 'database',
      success: ok && list.length > 0,
      results: list.length,
      duration_ms: Date.now() - startedAt,
      source: 'board',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiredFilled, triggerCatalog, searchGasHeaters, countElevation]);

  /* ---- the direct-fire BURNER BUILDER (js:744-1000) ----------------------
   *
   * The right-hand half of the Burner sub-tab, and the whole reason the tab
   * exists for a type-3 heater. It was stubbed by the port on the grounds that
   * `#assy_config.hide + #burner_config{display:none}`
   * (rating_mode_gas_heater.scss:579) hides the column anyway — true for type 1,
   * but `showBurnerAssy()` (js:1387-1394) drops the `.hide` for
   * `#type_id == 3` on every render, so on dev a direct-fire heater shows both
   * boxes. With every gas heater in the dev3 data being Heatco/type-1 at port
   * time the branch was never reachable; Midco is type 3, so it is now.
   *
   * Legacy's chain, entered by clicking into the Burner sub-tab
   * (`click #render_burner: rateBurner`, js:119):
   *
   *   rateBurner   PATCH the heater with rate:true and the burner's own field
   *                set (js:779-826), then ->
   *   renderBurner GET master gh_burner_assy scoped by the airflow and the
   *                burner_length_required_min the PATCH just computed
   *                (js:744-747); default assy_config_id to the saved one or
   *                the first row; draw the Burner Assembly list; then ->
   *   showBurner   assembly 1 ("User Defined") -> the heater's OWN burner rows,
   *                GET ahu/gas_heaters/burner/?gas_heater_id=<id>, editable,
   *                with + ADD and per-row delete. Any other assembly -> that
   *                assembly's fixed burner_config list, rendered into
   *                `#burner_config_content_static` with `.static` on the box
   *                (pointer-events:none, + ADD hidden). js:914-958.
   */
  const [burnerAssy, setBurnerAssy] = useState<IMasterRow[]>([]);
  const [burnerRows, setBurnerRows] = useState<BurnerRow[]>([]);
  const burnerKey = useRef(0);
  /** legacy `burner_data_click` (js:236/786/1589) — "the burner has been
   *  rated at least once", which is what gates the #rate button. */
  const burnerRated = useRef(false);
  /** legacy `burner_data_change` (js:797-806) — "a field the assembly list
   *  depends on has changed since the last rate". A DIRTY FLAG, not a latch:
   *  `rateBurner` only does its PATCH when it is set and clears it on the way
   *  in, and every `get_burner_assy`/`get_burner_assy_model` edit sets it
   *  again. The port previously read `burnerRated` for both jobs, which made
   *  the rate once-per-open and froze the assembly list at whatever the first
   *  one returned.
   *
   *  Starts SET, because legacy gets there before the user can reach the tab:
   *  `render()` calls `collectBurnerAssy()` on every render of a Midco heater
   *  (js:1182-1185), and that is what assigns `burner_data_change = true`. */
  const burnerDirty = useRef(true);
  /** not legacy's — `rateBurner` is a two-request await and the refresh effect
   *  below can re-enter it mid-flight, which legacy's synchronous
   *  render/flag dance cannot. */
  const burnerInFlight = useRef(false);
  const [triggerBurnerAssy] = useLazyGetGhBurnerAssyQuery();
  const [triggerBurners] = useLazyGetGasHeaterBurnersQuery();
  const [deleteBurner] = useDeleteGasHeaterBurnerMutation();

  /** the assembly rows unwrapped from their `{gh_burner_assy: {...}}` envelope. */
  const burnerAssyRows = useMemo(
    () =>
      burnerAssy
        .map((r) => (r as { gh_burner_assy?: Record<string, unknown> }).gh_burner_assy)
        .filter(Boolean) as Array<Record<string, unknown>>,
    [burnerAssy]
  );

  const loadUserDefinedBurners = useCallback(async () => {
    try {
      const rows = await triggerBurners(modelId, true).unwrap();
      burnerKey.current += rows.length;
      setBurnerRows(
        rows.map((r, i) => ({
          key: `saved-${r.id ?? i}`,
          id: r.id,
          burner_config_id: r.burner_config_id ?? undefined,
        }))
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[gas_heater selector] burner list failed', err);
    }
  }, [triggerBurners, modelId]);

  const rateBurner = useCallback(async () => {
    // `if (self.data.mfg_id != 50)` (js:782) — the burner rate is the
    // direct-fire path only. Guarded on the TYPE rather than the maker id so a
    // second direct-fire manufacturer would work too.
    if (!isDirectFire) return;
    // `burner_data_click = true` (js:786) runs BEFORE the dirty check, so
    // merely opening the Burner sub-tab arms the #rate button even when there
    // is nothing to re-rate.
    burnerRated.current = true;
    // `if (self.burner_data_change)` (js:804-806) — nothing to do when no
    // dependent field has moved since the last rate.
    if (!burnerDirty.current) return;
    if (burnerInFlight.current) return;
    burnerDirty.current = false;
    burnerInFlight.current = true;
    const s = state.current;
    try {
      const result = await rateGasHeater({
        id: modelId,
        data: {
          gas_heater_params: JSON.stringify(buildParamsBlob()),
          eadb_temp: s.eadb_temp,
          airflow_actual: s.airflow_actual,
          air_stream_id: s.air_stream_id,
          gas_inlet_pressure: s.gas_inlet_pressure,
          qty_wide: 1,
          qty_high: 1,
          power_id: s.power_id,
          conn_hand_id: s.conn_hand_id,
          venting_id: s.venting_id,
          weather_rating_id: s.weather_rating_id,
          burner_manifold_pressure_conditional: s.burner_manifold_pressure_conditional,
          ladb_temp_desired: s.ladb_temp_desired,
          fuel_type_id: s.fuel_type_id,
          firing_mode_id: s.firing_mode_id,
          gas_pilot_config_id: s.pilot_id,
          assy_height_from_floor: s.assy_height_from_floor,
          assy_distance_from_left_wall: s.assy_distance_from_left_wall,
          rate: true,
        },
      }).unwrap();
      // renderBurner(data.airflow_actual, data.burner_length_required_min)
      const rows = await triggerBurnerAssy(
        ghParamUrls.burner_assy(
          state.current.mfg_id as number,
          state.current.type_id as number,
          state.current.series_id as number,
          (result.airflow_actual as number | null) ?? null,
          // NOT `?? ''` — see ghParamUrls.burner_assy. A null required length
          // has to reach the backend as the string "null" so the list collapses
          // to User Defined, rather than as an empty string, which drops the
          // length filter and offers assemblies that are too short.
          (result.burner_length_required_min as number | null) ?? null
        ),
        true
      ).unwrap();
      setBurnerAssy(rows);
      // Which assembly ends up selected (js:757-770, dev PR #1366).
      //
      // The stored `assy_config_id` is honoured only when BOTH hold:
      //   - the heater carries a `master_gas_heater_id`, i.e. it has actually
      //     been rated and selected. Read off the PATCH response, which is
      //     legacy's `self.model.get(...)` — renderBurner runs inside the
      //     save's `.then`, so the model is the response.
      //   - that id is still in the list the rate just returned. A stored id
      //     that the airflow/length filters have since excluded is an
      //     assembly the burner cannot use, and re-selecting it would put the
      //     dialog into a state the Select could not reproduce.
      // Otherwise the list re-defaults, and it defaults PAST "User Defined" —
      // id 1, which sorts first and is the empty build-it-yourself entry — to
      // the first real assembly, falling back to row 0 when there is nothing
      // else (the min_length=null case, where User Defined is all there is).
      const assys = rows as Array<{ gh_burner_assy?: { id?: number; value?: string } }>;
      const stored = state.current.assy_config_id;
      const storedExists = assys.some(
        (r) => r.gh_burner_assy?.id != null && String(r.gh_burner_assy.id) === String(stored)
      );
      const keepStored =
        Boolean(result.master_gas_heater_id) && stored != null && storedExists;
      if (!keepStored && assys.length) {
        const pick =
          assys.find((r) => r.gh_burner_assy?.value !== 'user_defined') ?? assys[0];
        const id = pick?.gh_burner_assy?.id;
        if (id != null && String(id) !== String(stored)) {
          state.current = { ...state.current, assy_config_id: id };
          // the same reset the <select>'s own onChange does — the burner rows
          // on screen belong to the assembly we just moved off.
          setBurnerRows([]);
        }
      }
      bump();
    } catch (err) {
      burnerRated.current = false;
      // the rate did not happen, so the dependent fields are still unreflected
      // in the list; leave it dirty so the next trigger retries.
      burnerDirty.current = true;
      // eslint-disable-next-line no-console
      console.error('[gas_heater selector] burner rate failed', err);
    } finally {
      burnerInFlight.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirectFire, rateGasHeater, modelId, triggerBurnerAssy, bump]);

  /**
   * Legacy's `render()` tail (js:1175-1180):
   *
   *     if (self.burnerAssy) { if (self.burnerAssy.length > 0) { ...; self.rateBurner() } }
   *
   * Once the list has been fetched once, EVERY subsequent render re-enters
   * rateBurner — which is a no-op unless `burner_data_change` is set, so in
   * practice it means "re-rate and re-fetch the assemblies whenever one of
   * their inputs has moved". This effect is that hook: no dependency array, so
   * it runs after each render, and the dirty flag decides whether there is
   * work. Without it the list only ever reflected the state at the moment the
   * Burner sub-tab was first opened.
   */
  useEffect(() => {
    if (!isDirectFire) return;
    if (!burnerAssy.length) return;
    if (!burnerDirty.current) return;
    void rateBurner();
  });

  // showBurner's User-Defined branch: the heater's own burner rows.
  useEffect(() => {
    if (!isDirectFire) return;
    if (Number(state.current.assy_config_id) !== GH_BURNER_ASSY_USER_DEFINED) return;
    if (burnerRows.length) return;
    void loadUserDefinedBurners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirectFire, state.current.assy_config_id]);

  const addBurner = useCallback(() => {
    burnerKey.current += 1;
    const first = rowsOf(burnerConfigQ)[0] as { id?: number } | undefined;
    setBurnerRows((rows) => [
      ...rows,
      { key: `new-${burnerKey.current}`, burner_config_id: first?.id },
    ]);
  }, [burnerConfigQ]);

  const removeBurner = useCallback(
    async (row: BurnerRow) => {
      // a SAVED row is destroyed server-side (burner_model.destroy, js:973-980);
      // an unsaved one is just dropped, and losing the last one invalidates the
      // rated outputs (js:981-990).
      if (row.id != null) {
        try {
          await deleteBurner({ gas_heater_id: modelId, burner_id: row.id }).unwrap();
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('[gas_heater selector] burner delete failed', err);
          return;
        }
      } else {
        setRated(null);
        setRatedRow(null);
        setRatedCount(0);
      }
      setBurnerRows((rows) => rows.filter((r) => r.key !== row.key));
    },
    [deleteBurner, modelId]
  );

  // ---- SELECT (code-only, userArmed-gated) — searchGasHeaters(isSelect) ----
  const getFormShim = (): ComponentFormShim | undefined =>
    window.__FORM_SHIM__?.[modelId];

  const doSelect = useCallback(() => {
    userArmed.current = true;
    saveAbort.current?.abort?.();
    // Dev-sync J4 — the Checkpoint-4 switch (gas_heater.selector.js:
    // 1042-1049): the Select goes through POST gas_heaters/<id>/select, which
    // performs the same partial_update semantics as the plain PATCH (all 8
    // cascade endpoints take the resource's own PATCH body verbatim — survey
    // §A2) and additionally runs the downstream cascade, returning
    // {updated, cascade}. No autoRefresh flag — a non-empty cascade re-reads
    // the rows it changed (guardSelect default).
    //
    // ★ selectGasHeater is TWO writes on dev, and which body goes to which one
    // decides what the cascade sees. `/select` gets the CATALOG ROW spread
    // (:1026-1029) — master_gas_heater_id plus a pile of master-catalog keys
    // the serializer ignores — and the real persist happens afterwards, inside
    // onCommit, as the ordinary searchGasHeaters PATCH (:2063-2110). So the
    // cascade snapshot on dev straddles a write that can only move
    // master_gas_heater_id; eadb_temp / airflow_actual / qty_* land later, on a
    // PATCH nothing is watching.
    //
    // This port had collapsed the two into one, posting the whole GhRatePayload
    // to `/select`. Same persisted field set, but now the FULL selection lands
    // inside the snapshot window, so cascade_helpers sees entering_db and
    // leaving_db_actual move and returns a non-empty cascade. That used to mean
    // `window.location.reload()` — the reported "the page refreshes on dev3 but
    // not on dev", guaranteed on a heater's first Select where both values go
    // null → rated, while dev posts no eadb_temp, leaves ladb_temp_actual null
    // through its `/select` and cascades to nothing. guardSelect now answers a
    // cascade with an in-place re-read instead (cascadeRefresh.ts), so the
    // split no longer decides whether the board survives a Select — but keep
    // it anyway: it is what legacy does, and a cascade that fires on a write
    // dev never cascades on still re-rates downstream components dev leaves
    // alone. Split back into legacy's two writes.
    //
    // Everything the writes need is snapshotted HERE, synchronously, because
    // the dialog closes (and this component unmounts) at the bottom of this
    // function — before the POST resolves. Legacy does exactly the same, and
    // for the same reason: dev eed97f68ac ("Close rating dialogs immediately
    // on Select instead of waiting on cascade_warning POST") moved
    // `self.remove()` out of guardSelect's onCommit and up to the end of
    // selectGasHeater, snapshotting the DOM-backed fields first
    // (gas_heater.selector.js:985-995). React's payload is built off
    // `state.current`, so the snapshot is just these two calls.
    // Both writes carry the SAME catalog row the output sheet is showing
    // (legacy searchGasHeaters(gas_heaters_list, rated index, isSelect) —
    // js:2063-2078).
    const chosen = catalog[ratedIndex] ?? ratedRow;
    // the `/select` body — legacy's `$.extend({}, ratedHeater, {
    // master_gas_heater_id: ratedHeater.id})` (js:1026-1029). An empty catalog
    // page leaves both undefined, which JSON.stringify drops, so the stored
    // selection survives — as on dev.
    const selectPayload = {
      ...((chosen ?? {}) as Record<string, unknown>),
      master_gas_heater_id: (chosen as { id?: unknown } | null | undefined)?.id,
    };
    // …and the real persist, unchanged: the GhRatePayload with rate:false +
    // isSelect:true that searchGasHeaters sends from inside onCommit.
    const patchPayload = buildPayload(true, chosen);
    const section = activate.section as { id?: number; tunnel_id?: number } | undefined;
    const seasonId = Number(seasonIdRef.current) || null;
    // The owning form's shim lives on the BOARD, not in this dialog, so it
    // survives the unmount and can still take the saved row below.
    const shim = getFormShim();
    const selectChain = (async () => {
      try {
        await guardSelect({
          url: `/api/v1.0/ahu/gas_heaters/${modelId}/select`,
          analyticsComponent: 'gas_heater',
          payload: selectPayload,
        });
        // Dev-sync J6 — doSelect's section season persist (gas_heater.
        // selector.js:1012-1020, FIRST thing inside onCommit, i.e. after the
        // /select resolves): `section.save({season_id: Number(staged) || null},
        // {patch:true})` — a direct Backbone save that bypasses the section
        // model's autosave side-effects (no validate run, no
        // changed:section event), so here it is a bare section PATCH.
        if (section?.id != null) {
          queueSectionPatch({
            dispatch,
            sectionId: section.id,
            tunnelId: Number(section.tunnel_id),
            changes: { season_id: seasonId },
          });
        }
        // …then the persisting PATCH, which is what actually writes the
        // selection on dev (searchGasHeaters with isSelect, js:2063-2110).
        // Deliberately NOT registered on `saveAbort`: the dialog is already
        // closing and nothing may abort this one.
        const result = (await rateGasHeater({
          id: modelId,
          data: patchPayload,
        }).unwrap()) as Record<string, unknown> | null;
        if (result) {
          // no-op once the dialog has closed (this component is gone); still
          // correct if it has not — e.g. a failed/absent hide.
          setRated(result as IGasHeaterRow);
          // re-render the owning form (shim) with the saved selection.
          shim?.model.set(result);
          shim?.render();
        }
        // components-only validation for this section's tunnel (legacy).
        ahuBoardBus.emit('validate:tunnel:components:only', {
          tunnelId: section?.tunnel_id,
          sectionId: section?.id,
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[gas_heater selector] select failed', err);
      }
    })();
    // …and a dialog reopened before that chain settles waits for it rather than
    // seeding from the pre-select row (selectWriteGate.ts). The selection only
    // exists on the server once BOTH writes above land, and the dialog closes
    // two lines down — so without this a reopen inside that window reads a
    // genuinely unrated row and draws no output sheet.
    trackSelectWrites(selectWriteKey('gas_heaters', modelId), selectChain);
    void selectChain;
    // hide cascade — fired NOW, not after the POST (dev eed97f68ac).
    ahuBoardBus.emit('rating_mode:hide');
    ahuBoardBus.emit(`rating_mode:hide:${modelId}`, { selected: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, buildPayload, activate, dispatch, catalog, ratedIndex, ratedRow, rateGasHeater]);

  // ---- PRINT (code-only, userArmed-gated) — the ONLY celery flow ----
  const doPrint = useCallback(async () => {
    userArmed.current = true;
    const m = (rated ?? detail ?? {}) as IGasHeaterRow;
    const ahu = activate.ahu as { tag?: string; remarks?: string } | undefined;
    // collectDataForPDF (legacy) — the report envelope. We send the REAL model
    // fields (the legacy version hard-codes a demo payload; we send the live
    // model so the PDF reflects this heater).
    const data: Record<string, unknown> = {
      report_file_name: 'gas_heater_selection',
      tag: ahu?.tag,
      remarks: ahu?.remarks,
      id: m.id,
      qty_wide: m.qty_wide,
      qty_high: m.qty_high,
      airflow_actual: m.airflow_actual,
      airflow_std: m.airflow_std,
      face_velocity_actual: m.face_velocity_actual,
      face_velocity_std: m.face_velocity_std,
      eadb_temp: m.eadb_temp,
      ladb_temp_actual: m.ladb_temp_actual,
      apd: m.apd,
      input_rating_std_total: m.input_rating_std_total,
      output_rating_std_total: m.output_rating_std_total,
      master_gas_heater_id: m.master_gas_heater_id,
    };
    let celeryTaskId: string | null = null;
    const socket = getSocket();
    // ★ [NEG]#79 FIX: REAL equality `data.task_id === celery_task_id` (the
    // legacy `if (celery_task_id, data.task_id)` is a comma operator that fires
    // on ANY task completion). Register BEFORE the POST resolves; cleaned up
    // after the matching message or on unmount.
    const onData = (msg: SocketDataMessage) => {
      const incomingTaskId = (msg as { task_id?: string }).task_id;
      if (celeryTaskId == null || incomingTaskId !== celeryTaskId) return;
      const payload = (msg.data ?? {}) as { generate_id?: string | number; file?: string };
      const generateId = payload.generate_id;
      const file = payload.file;
      if (generateId != null && file) {
        window.open(`${window.location.origin}/media/report_export/${generateId}/${file}`);
      }
      socket.off('data', onData);
    };
    socket.on('data', onData);
    try {
      const { task_id } = await requestPdf(data).unwrap();
      celeryTaskId = task_id;
    } catch (err) {
      socket.off('data', onData);
      // eslint-disable-next-line no-console
      console.error('[gas_heater selector] pdf request failed', err);
    }
  }, [rated, detail, activate, requestPdf]);

  /* ---- RESET (gas_heater_<id>:selector:reset — gas_heater.selector.js:1573)
   *
   * The circular-arrow button in the dialog header ("Reset to defaults
   * values"), between Print and RATE. Legacy's `reset:` does six things:
   *
   *   1. keep the CURRENT Manufacturer (`$el.find('select.params_gas_heater
   *      [name="mfg_id"]').val() || gasHeaterDefaults.mfg_id`, :1575) and
   *      replace the WHOLE param blob with the defaults:
   *      `model_data.params_gas_heater = {...gasHeaterDefaults, mfg_id}` (:1578)
   *   2. clear the "the user touched this" flags (:1579-1589)
   *   3. `$('#for-output-rendering')[0].innerHTML = ''` (:1591) — empty the
   *      RATED OUTPUT pane
   *   4. `saveRequest?.abort?.()` (:1592) — drop any in-flight save
   *   5. `model.set({master_gas_heater_id: null, model: null, rate: true})`
   *      (:1593-1597) plus four `model_data.model` nulls (:1598-1601)
   *   6. re-select the kept mfg in the DOM and `$select.trigger('change')`
   *      (:1603-1608), i.e. run `changeParams` for mfg_id → `getCollections`,
   *      which re-walks type → series → heatex/venting/pilot/configuration/
   *      burner/fuel/firing from the top and re-picks each list's first row.
   *
   * WHAT MAPS TO WHAT HERE
   *
   * - `model_data.params_gas_heater` vs `model_data.model`. Legacy keeps those
   *   two halves in separate objects and reset wipes only the FIRST; React
   *   holds one FLAT `state.current`, so a wholesale
   *   `state.current = {...GH_DEFAULTS}` would also throw away Airflow, EADB,
   *   desired LADB, Pricing, the Assy Height/Distance and the staged season —
   *   none of which legacy touches. So we delete exactly the keys whose control
   *   carries `class="params_gas_heater"` in gas_heater.selector.tmpl
   *   (:129/147/164/203/223/243/268/290/315/355/372/389/449/469/489/536/803/
   *   810/828/1023) and leave every `model_field` key alone.
   *   `assy_config_id` is deliberately NOT in that list: legacy reads the
   *   burner assembly off `model_data.model.assy_config_id` for the save
   *   (:759-764, :2018) and reset never nulls it.
   *
   * - step 6, `$select.trigger('change')`. There is no cascade *call* to make
   *   here: this selector's cascade is declarative. Every param-chain GET is
   *   keyed on the LIVE `state.current.mfg_id/type_id/series_id` (see the
   *   "param-chain GETs for the LIVE selection" block above), and the two
   *   adoption effects below — the DOM-default sweep and the
   *   `keepElseFirst('type_id'|'series_id')` re-cascade, both of which run on
   *   EVERY render with no dep list — are the port of `getCollections`'
   *   auto-pick. Writing the defaults into `state.current` and calling `bump()`
   *   therefore re-keys the queries and re-runs the identical cascade, which is
   *   what `trigger('change')` bought legacy. Nothing extra to fire.
   *
   * - the flags at :1579-1589 have no React counterpart BY CONSTRUCTION and so
   *   are nothing to clear: `seriesIdChange` is already documented as a no-op
   *   here (see the re-cascade block below), `fuelTypeIdChange`/
   *   `firingModeIdChange`/`powerIdChange` are only read inside commented-out
   *   legacy code (:698), `output_rating_std_changed` +
   *   `is_output_rating_std_selected` gate legacy's calcRating re-pick which
   *   the port replaced with the unconditional keep-else-first effect, and
   *   `selectedGasHeater` is the isSelect argument here (buildPayload). The two
   *   that DO have counterparts are `temporary`/`output_rating_total` (the
   *   rated-output state, cleared below) and `burner_data_click`
   *   (`burnerRated`).
   *
   * - the active-tab preservation (`activeTabId` / `activeParnetTabId` [sic],
   *   :1576/:1601/:1610-1611) is a no-op in React and needs no port: legacy
   *   captures and restores it because `render()` re-draws the whole dialog
   *   from the template mid-reset and would otherwise lose the highlight, while
   *   here `activeTab`/`activeSubTab` are ordinary component state that this
   *   callback never writes. The one case legacy's restore does NOT cover —
   *   the open tab ceasing to exist because the type changed — is already
   *   handled by the `inputTabs.includes(activeTab)` fallback effect below
   *   (legacy `checkSubTab`).
   *
   * headerMode: this selector is BOARD-ONLY (the header chassis renders
   * `li.hide` for gas_heater) and does not take the prop at all, so there is no
   * header guard to add. Reset writes nothing to the server in either case —
   * it only edits `state.current` and the local rated-output state. */
  const doReset = useCallback(() => {
    // :1575 — keep whatever Manufacturer is showing.
    const mfg = state.current.mfg_id ?? GH_DEFAULTS.mfg_id;

    // :1578 — the `.params_gas_heater` half of the flat state, dropped and
    // re-seeded from gasHeaterDefaults. Keys absent from GH_DEFAULTS are left
    // undefined on purpose: the DOM-default sweep re-picks each one from its
    // freshly re-keyed list, exactly as legacy's re-rendered <select> lands on
    // option 0.
    const next = { ...state.current };
    for (const key of GH_PARAM_KEYS) delete next[key];
    Object.assign(next, GH_DEFAULTS, { mfg_id: mfg });

    // :1598-1601 — the four `model_data.model` nulls. `firing_mode_id`,
    // `fuel_type_id` and `gas_pilot_config_id` (`pilot_id` here) are param keys
    // too and were already cleared above — GH_DEFAULTS re-seeds fuel_type_id to
    // 1, matching legacy, whose params half wins over the model null for what
    // the select renders. Only the manifold pressure is model-only.
    next.burner_manifold_pressure_conditional = null;

    state.current = next;
    // `init` mirrors the last committed baseline (seed effect above); kept in
    // sync so a later reader cannot see a pre-reset snapshot.
    init.current = { ...state.current };

    // :1591-1597 — empty #for-output-rendering and null master_gas_heater_id /
    // model. `<GasHeaterRatedOutputs>` early-returns on a null `model`
    // (GasHeaterRatedOutputs.tsx:230), so clearing `rated` IS the innerHTML=''.
    setRated(null);
    setRatedRow(null);
    setRatedCount(0);
    setRatedIndex(0);
    setCatalog([]);
    ratedDatetime.current = null;
    // :1589 `burner_data_click = false` — re-arms the direct-fire Burner
    // sub-tab so opening it rates the burner again.
    burnerRated.current = false;
    // legacy leaves `burner_data_change` alone here, but the render that
    // follows the reset runs `collectBurnerAssy()` for a Midco heater
    // (js:1182-1185) and sets it — the params just went back to defaults, so
    // the list has to be re-fetched against them.
    burnerDirty.current = true;
    // :1592 — drop any in-flight save (same abort the `:selector:cancel`
    // subscription below uses).
    saveAbort.current?.abort?.();

    // checkButtonsAvailability (:2583) with master_gas_heater_id and model both
    // null → #select (and #print, which rides the same condition) go
    // non-clickable. The `rated`-driven effect above would do this on the next
    // render anyway; doing it here keeps the chassis' synchronous
    // `hasChanges = selectEnabledRef.current` read (ComponentSelectorDialog
    // :353) correct for the markChanged() on the very next line.
    setSelectEnabled(false);
    setPrintEnabled?.(false);
    markChanged();
    // re-key the param-chain GETs → the cascade effects re-run (step 6 above).
    bump();
    // Deps are complete (every other identifier here is a ref, a useState
    // setter or a module constant), so unlike doRate/rateBurner above this one
    // needs no exhaustive-deps escape hatch.
  }, [setSelectEnabled, setPrintEnabled, markChanged, bump]);

  // ---- chassis fan-out subscriptions (§30.4) ----
  useEffect(() => {
    const offs = [
      ahuBoardBus.on(
        `gas_heater_${modelId}:selector:rate` as `${string}_${number}:selector:rate`,
        () => void doRate()
      ),
      ahuBoardBus.on(
        `gas_heater_${modelId}:selector:select` as `${string}_${number}:selector:select`,
        () => void doSelect()
      ),
      ahuBoardBus.on(
        `gas_heater_${modelId}:selector:print` as `${string}_${number}:selector:print`,
        () => void doPrint()
      ),
      ahuBoardBus.on(
        `gas_heater_${modelId}:selector:reset` as `${string}_${number}:selector:reset`,
        () => doReset()
      ),
      ahuBoardBus.on(
        `gas_heater_${modelId}:selector:cancel` as `${string}_${number}:selector:cancel`,
        () => {
          // cancelChanges: abort any in-flight save; the chassis fires the hide
          // + re-fetches the model via RTKQ cache. No write here.
          saveAbort.current?.abort?.();
        }
      ),
      ahuBoardBus.on('cancel:dialog:changes', () => {
        saveAbort.current?.abort?.();
      }),
    ];
    return () => offs.forEach((off) => off());
  }, [modelId, doRate, doSelect, doPrint, doReset]);

  // ---- body-keydown handler (legacy $('body').keydown(self.keydown) LEAK) ----
  // Ported as a PROPERLY CLEANED-UP effect (the legacy never reliably removes
  // it). It only drives the heat-exchanger configuration picker (↑/↓ arrows);
  // gated on the configuration tab being active. Read-only smoke never triggers
  // it (it changes no model field — only the visual config row selection).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (document.querySelector('.popup-wrapper.active')) return;
      if (!document.querySelector('.heat_exchanger_more.heat_exchanger-tab.active')) return;
      if (e.keyCode !== 38 && e.keyCode !== 40) return;
      // config-picker arrow nav is a visual-only concern (no model write); the
      // detailed DOM walk is deferred with the dependency cascade. We stop the
      // event so it doesn't bubble to the canvas nav, matching legacy.
      e.stopPropagation();
      e.preventDefault();
    };
    document.body.addEventListener('keydown', handler);
    return () => document.body.removeEventListener('keydown', handler);
  }, []);

  // abort any in-flight save on unmount (data-safety).
  useEffect(() => () => saveAbort.current?.abort?.(), []);

  // ---- render ----
  // NB the loading gate lives at the BOTTOM of this body, immediately above
  // `return (`, not here: the DOM-default adoption further down is a hook, and
  // returning the preloader before it would change the hook count between the
  // loading and loaded renders. Everything between here and the gate is either
  // a component definition or a null-safe derivation of query data, so it is
  // harmless to evaluate while the GET is still in flight.
  const s = state.current;

  // option helpers --------------------------------------------------------
  //
  // EMPTY OPTION LIST ⇒ `non-clickable` ON THE WHOLE ROW (js:1128-1137). After
  // every render legacy walks `select[name]` and, for any select with no
  // `<option>` children, climbs four parents — col-xs-12 → row → input-part →
  // row — to put `non-clickable` (`pointer-events:none;opacity:.6`,
  // ahu/styles.css:99) on the outer `.row`, greying the LABEL as well as the
  // control:
  //
  //     if ($(el).has('option').length < 1) {
  //       if (el.id != 'input_rating_std' && el.id != 'wide' && el.id != 'high')
  //         $(el).parent().parent().parent().parent().addClass('non-clickable')
  //     } else { …removeClass('non-clickable') }
  //
  // Ported as an `emptyOptions` prop on Row rather than a DOM sweep. Two things
  // decide the flag, and both come from what LEGACY would have rendered:
  //
  //  - the data rows only. This port adds `<option>` nodes legacy has no
  //    equivalent for — the blank placeholder a controlled select needs while
  //    its value is '', and the value-not-in-rows fallback that keeps a saved
  //    value visible — and counting those would leave every empty row clickable.
  //  - EXCEPT where the legacy template itself hardcodes an option, which makes
  //    `has('option')` true regardless of the collection. Heatex Qty is the only
  //    one (`<option value="">- -</option>`, tmpl:390), so it never greys out.
  //
  // Legacy's three id exclusions (`input_rating_std`, `wide`, `high` — Available
  // Heater Sizes and the Qty Wide x High pair) are honoured by simply not
  // passing the prop on those rows.
  const docList = (field: string): OptionRow[] =>
    ghCmsOptionList(ghDoc as GasHeaterCmsDoc, globalDoc as GasHeaterCmsDoc, field);

  /** A doc-resolved <select> (mfg/power/conn_hand/cert/blank_off/air/weather).
   *
   * `select_gas_heater` is load-bearing, not decoration: it is the ONLY rule
   * that gives these selects a width (`width:calc(100% - 25px)!important;
   * line-height:27px` — rating_mode_gas_heater.scss:405). The global `select`
   * rule (common.css:29092) sets no width at all, so a select without this
   * class shrinks to its longest option and the dialog renders a ragged
   * column of differently-sized dropdowns instead of the legacy grid.
   *
   * RENDER FUNCTION, NOT A COMPONENT — call it as `docSelect({...})`, never as a
   * JSX element. It closes over `s`/`docList`/`onParamField`, so it has to live
   * inside the component body; declaring it here makes it a NEW component type on
   * every render, and React reconciles by type identity, so as a JSX element it
   * would unmount and remount its <select> on every re-render. Every commit
   * (`onParamField`/`onModelField` → `bump()`) is a re-render, so the field you
   * just tabbed into was being destroyed under the caret and focus fell to
   * <body> — the Tab-navigation bug. Legacy's `change .model_field`
   * (gas_heater.selector.js:1637) never re-renders the pane at all. Called as a
   * plain function the returned <select> is spliced into the parent's children
   * at the same position, so React updates the existing DOM node in place. */
  const docSelect = ({
    name,
    fieldClass = 'select_gas_heater search_field params_gas_heater collections_change_field',
    none,
  }: {
    name: string;
    fieldClass?: string;
    none?: boolean;
  }) => {
    const value = s[name];
    const rows = docList(name);
    const valStr = value == null || value === '' ? '' : String(value);
    const hasValueRow = rows.some((r) => String(r.id) === valStr);
    const selectedLabel =
      valStr !== ''
        ? ghCmsSelectedLabel(ghDoc as GasHeaterCmsDoc, globalDoc as GasHeaterCmsDoc, name, value as number)
        : '';
    return (
      <select
        name={name}
        data-name={name}
        className={fieldClass}
        value={valStr}
        // Selector-parity — route by the class the caller asked for. A
        // `.model_field` select is legacy's `change .model_field`
        // (gas_heater.selector.js:1637), NOT `changeParams`; only the latter
        // clears the rated output. Hardcoding onParamField here made
        // onModelField's exclusion list (air_stream/gas_inlet/assy_*, the port
        // of js:1660) dead code for Air Stream, so picking a stream wiped an
        // already-selected heater.
        onChange={(e) =>
          fieldClass.includes('model_field')
            ? onModelField(name, e.target.value)
            : onParamField(name, e.target.value)
        }
      >
        {none && <option value="" data-value="None">--</option>}
        {!none && valStr === '' && <option value="" />}
        {rows.map((r) => (
          <option key={String(r.id)} value={String(r.id)} data-value={String(r.label ?? '')}>
            {String(r.label ?? '')}
          </option>
        ))}
        {!none && valStr !== '' && !hasValueRow && (
          <option value={valStr}>{selectedLabel || valStr}</option>
        )}
      </select>
    );
  };

  /** A param-GET-resolved <select> (type/series/heatex/venting/pilot/etc.).
   * Render function, not a component — see the note on `docSelect`. */
  const paramSelect = ({
    name,
    rows,
    valueKey = 'id',
    labelKey,
    scalar,
    fieldClass = 'select_gas_heater search_field params_gas_heater collections_change_field',
    style,
    id,
    emptyOption,
    strictRows,
  }: {
    name: string;
    rows: OptionRow[] | undefined;
    valueKey?: string;
    labelKey?: string;
    /** scalar list: rows are bare values (tube_rows/configuration). */
    scalar?: boolean;
    fieldClass?: string;
    style?: React.CSSProperties;
    id?: string;
    /** a leading placeholder option legacy renders unconditionally (heatex
     * Qty's `<option value="">- -</option>`, tmpl:390). */
    emptyOption?: string;
    /** drop the value-not-in-rows fallback option. That option is a port
     * addition that keeps a saved selection visible through the load window;
     * for Available Heater Sizes the label only exists as a row of the list
     * (`input/output`), so a value the list has lost would render as a bare
     * number where legacy renders nothing. */
    strictRows?: boolean;
  }) => {
    const value = s[name];
    const valStr = value == null || value === '' ? '' : String(value);
    const list = rows ?? [];
    const hasValueRow = scalar
      ? list.some((r) => String(r) === valStr)
      : list.some((r) => String((r as OptionRow)[valueKey]) === valStr);
    return (
      <select
        name={name}
        id={id}
        data-name={name}
        className={fieldClass}
        style={style}
        value={valStr}
        onChange={(e) => onParamField(name, e.target.value)}
      >
        {emptyOption != null ? (
          <option value="">{emptyOption}</option>
        ) : (
          valStr === '' && <option value="" />
        )}
        {scalar
          ? (list as unknown as Array<string | number>).map((v) => (
              <option key={String(v)} value={String(v)}>
                {String(v)}
              </option>
            ))
          : list.map((r) => (
              <option key={String((r as OptionRow)[valueKey])} value={String((r as OptionRow)[valueKey])}>
                {optionLabel(r as OptionRow, labelKey)}
              </option>
            ))}
        {valStr !== '' && !hasValueRow && !strictRows && (
          <option value={valStr}>{valStr}</option>
        )}
      </select>
    );
  };

  /** Render function, not a component — see the note on `docSelect`. */
  const numInput = ({
    name,
    formatter,
    readOnly,
    fieldClass = 'model_field input_gas_heater',
    style,
    skipTabStop,
    dataType,
    id,
    dataMin,
    dataMax,
    dataStep,
  }: {
    name: string;
    formatter: 'integer' | 'decimal';
    readOnly?: boolean;
    fieldClass?: string;
    style?: React.CSSProperties;
    /** legacy carries `tabindex="-1"` on a handful of EDITABLE fields, which
     * keeps them clickable but out of the Tab order. Read-only fields get it
     * unconditionally below; this is for the editable ones. */
    skipTabStop?: boolean;
    /** legacy's `data-type="number"`. Not decoration: `common.css:28900`
     * (`input[data-type=number]`) is what right-aligns the value and sets its
     * padding — the only way legacy aligns a field it does NOT give `text-right`
     * to. Opt-in, because the fields that DO carry `text-right` carry no
     * `data-type` and must not pick up its padding. */
    dataType?: 'number';
    /** legacy stamps `id` on the handful of fields it later re-selects by id
     * (e.g. `input[id="burner_manifold_pressure_conditional"]`,
     * gas_heater.selector.tmpl:521). Opt-in — most fields carry no id. */
    id?: string;
    /** `data-min`/`data-max`/`data-step`, read generically by legacy's
     * changeModelField (js:1639-1643) to clamp the committed value. Opt-in —
     * only fields with a real range (e.g. Manifold Pressure) pass these. */
    dataMin?: number;
    dataMax?: number;
    dataStep?: number;
  }) => {
    const v = s[name];
    const display =
      formatter === 'integer' ? formatIntegerDisplay(v) : formatDecimalDisplay(v);
    // Legacy commits numeric inputs on CHANGE, never on input: the selector
    // binds `change .model_field` (gas_heater.selector.js:99) and
    // `change .capacity_required_total_change` (:122), and formatter.js's
    // NumericInputFormatter deliberately leaves `oninput` commented out
    // ("Don't doing anything while editing") while `encode()` returns the raw
    // value whenever the element is document.activeElement — so dev neither
    // re-formats nor re-renders until you blur or press Enter.
    //
    // The port bound value+onChange instead, so every keystroke ran
    // onModelField -> bump() -> a full re-render of this component, which (see
    // `docSelect`) remounted the <input> and took the caret with it. Typing also
    // round-tripped through formatIntegerDisplay ("6000" -> "6,000" -> next key
    // -> "6,0000" -> NaN).
    //
    // Mirror legacy (and CoilSelector.tsx:2062, same defect fixed there): free
    // typing on an uncontrolled input, commit + format on blur/Enter. An
    // unchanged value must NOT commit — a bare focus/blur pass would otherwise
    // null out the rated outputs. Read-only fields stay controlled so engine
    // writes keep repainting them.
    if (readOnly) {
      return (
        <input
          type="text"
          name={name}
          data-name={name}
          data-type={dataType}
          data-formatter_type={formatter}
          autoComplete="off"
          className={`${fieldClass} non-clickable`}
          style={style}
          readOnly
          tabIndex={-1}
          value={display}
          onChange={() => undefined}
        />
      );
    }
    return (
      <input
        type="text"
        id={id}
        name={name}
        data-name={name}
        data-type={dataType}
        data-formatter_type={formatter}
        data-min={dataMin}
        data-max={dataMax}
        data-step={dataStep}
        autoComplete="off"
        className={fieldClass}
        style={style}
        tabIndex={skipTabStop ? -1 : undefined}
        defaultValue={display}
        // Now that the node survives a re-render, `defaultValue` alone would
        // freeze it at its mount value — engine/interlock writes (ghTempInterlock
        // moving Desired LADB when EADB changes, the seed effect filling the pane
        // once the GETs land) would never reach the DOM. Push them in through the
        // ref instead, skipping the input the user is currently in: that is
        // exactly formatter.js's `encode()` guard ("return the raw value whenever
        // the element is document.activeElement"), so an in-progress edit is
        // never reformatted under the caret. The inline arrow makes React re-run
        // this on every render, which is the sync point we want.
        ref={(el) => {
          if (el && document.activeElement !== el && el.value !== display) {
            el.value = display;
          }
        }}
        onBlur={(e) => {
          if (e.target.value !== display) onModelField(name, e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
        }}
      />
    );
  };

  /** Legacy pane classes (tmpl:100/675/878/985/1037/1287). Every pane but the
   * Heater one carries `col-xs-12 float-left`; the Heater pane deliberately
   * carries only `float-left` (tmpl:100). Hiding is by class alone
   * (`.fade{display:none}` / `.active.in.fade{display:block}`,
   * common.css:18499) — NOT by `.tab-content > .tab-pane`, which is why the
   * legacy `.row` wrapper between `.tab-content` and the panes is safe. */
  const tabClass = (t: TabName) =>
    `tab-pane fade scrollable-content${t === 'Heater' ? '' : ' col-xs-12'}${
      activeTab === t ? ' active in' : ''
    } float-left`;

  const subTabClass = (t: SubTabName) =>
    `tab-pane fade${activeSubTab === t ? ' in active' : ''} sub_tab` +
    ' scrollable-content col-xs-12 float-left';

  const voltageRows = Number(s.type_id) === 1 ? docList('power_id') : rowsOf(voltagesQ);

  const inputTabs: TabName[] = isDirectFire
    ? ['Heater', 'Performance', 'Mounting', 'Pricing']
    : ['Heater', 'Performance', 'Mounting', 'Options', 'Bypass', 'Pricing'];

  // showBurner's two branches (js:914-958): assembly 1 ("User Defined") gives
  // the editable list, anything else the assembly's own fixed burner_config
  // array under `.static`.
  const isUserDefinedAssy =
    Number(s.assy_config_id ?? GH_BURNER_ASSY_USER_DEFINED) === GH_BURNER_ASSY_USER_DEFINED;
  const staticBurnerConfigs = isUserDefinedAssy
    ? []
    : ((burnerAssyRows.find((a) => Number(a.id) === Number(s.assy_config_id))
        ?.burner_config as Array<Record<string, unknown>> | undefined) ?? []);

  // …and if the tab that just disappeared was the open one, fall back rather
  // than leaving the pane blank with nothing highlighted. Legacy re-renders the
  // whole dialog from the template and `checkSubTab` restores properties_more
  // when no sub-tab pane carries `active` (js:764-770); switching an
  // Indirect Fire heater to Midco while Options/Bypass or the Heat Exchanger
  // sub-tab was open is exactly that case.
  useEffect(() => {
    if (!inputTabs.includes(activeTab)) setActiveTab('Heater');
    if (isDirectFire && activeSubTab === 'heat_exchanger_more')
      setActiveSubTab('properties_more');
  });

  const configurationLetters = (rowsOf(configurationQ) as unknown as Array<
    string | number
  >).map(String);
  const selectedConfiguration =
    s.configuration == null ? '' : String(s.configuration);

  // Altitude = the PROJECT's elevation (legacy `{{project.elevation}}`,
  // tmpl:714 — the same value `self.project.get('elevation')` feeds the type-1
  // catalogue query at js:2554). `activate.project` is never populated: the
  // board's only `rating_mode:activate` emitter (useComponentForm
  // .openRatingStub) sends `{name, loading, current_model, section, section2}`
  // and no project, so this read was always `undefined` and the field rendered
  // blank on every unit. Fetch the project the way CoilSelector already does
  // (:948) — the board bootstrap has it cached, so this is a warm read.
  //
  // The `?? 0` matches what dev shows rather than what the template literally
  // says: `Project.save()` coerces a null elevation to 0 (project/models.py
  // :1964-1965), so a legacy project effectively always has a number here and
  // the blank state is unreachable there. Rows that predate that coercion (or
  // arrive without it) render "0" instead of an empty box.
  const { data: boardProject } = useGetAhuBoardProjectQuery(
    (ahu as { project_id?: number } | null | undefined)?.project_id ?? skipToken
  );
  const projectElevation =
    (boardProject as Record<string, unknown> | undefined)?.elevation ??
    (activate.project as Record<string, unknown> | undefined)?.elevation ??
    0;

  // live Capacity Required total (recomputed as the user edits airflow / EADB /
  // desired LADB — legacy's `.capacity_required_total_change` handler). Same
  // number the size query above divides by qty_wide × qty_high.
  const capacityRequiredTotal = capacityTotal;

  // 1..max_qty_wide / 1..max_qty_high (legacy getQty). Direct fire (type 3)
  // answers `{max_qty_wide: null, max_qty_high: null}`, which legacy's
  // `for (i = 1; i <= null; i++)` turns into an empty list — `range` below does
  // the same for null/undefined.
  const qtyLimits = qtyQ.data as
    | { max_qty_wide?: number | null; max_qty_high?: number | null }
    | undefined;
  const range = (max: number | null | undefined) =>
    Array.from({ length: Math.max(0, Number(max) || 0) }, (_, i) => i + 1);
  const qtyWideRows = range(qtyLimits?.max_qty_wide) as unknown as OptionRow[];
  const qtyHighRows = range(qtyLimits?.max_qty_high) as unknown as OptionRow[];

  // Available Heater Sizes options: value = output_rating_std, label =
  // `<input_rating_std>/<output_rating_std>` (legacy calcRating, js:1431-1440).
  // `if (row.output_rating_std)` there is a TRUTHY test, so a 0 rating is
  // dropped along with a null one.
  const outputRatingRows = (capacityEnabled ? rowsOf(outputRatingStdQ) : [])
    .filter((r) => Boolean((r as Record<string, unknown>).output_rating_std))
    .map((r) => {
      const row = r as Record<string, unknown>;
      return {
        id: row.output_rating_std,
        name: `${row.input_rating_std}/${row.output_rating_std}`,
      } as unknown as OptionRow;
    });
  outputRatingRowsRef.current = outputRatingRows as unknown as Array<{ id?: unknown; name?: unknown }>;
  // legacy's `only_output_rating` — the bare values, used both for the
  // auto-pick below and for the "none" fallback in the JSX.
  const outputRatingValues = outputRatingRows.map((r) =>
    Number((r as unknown as Record<string, unknown>).id)
  );
  // legacy swaps the whole select for a readonly `value="none"` input when the
  // capacity query came back with nothing (js:1448-1450). It does NOT do that
  // when the query never ran (blank airflow/EADB/LADB, or mfg 188) — there it
  // just leaves the select empty — so this is gated on the query having run.
  const noHeaterSizes =
    capacityEnabled && !outputRatingStdQ.isFetching && outputRatingRows.length === 0;

  // Venting and Firing Mode. Hoisted so the JSX below and the DOM-default
  // adoption underneath cannot drift apart.
  //
  // `collections` starts as `{ mfgs }` and nothing else (js:333), so Venting
  // has NO static fallback on dev: `collections.venting` is only ever the
  // `params/venting/` result (js:590-591), and an empty result leaves the
  // select with no options — which the non-clickable sweep then greys out.
  // The port fell back to the master `gas_heater_venting/` collection, so a
  // maker with no CMS venting rows (Midco: `params/venting/?mfg_id=188&
  // type_id=3&series_id=1068` → `[]`) offered all eight venting types where
  // dev offers none.
  const ventingRows = rowsOf(ventingParamQ);
  // Firing Mode does have one, but it is direct-fire-only: calcRating assigns
  // the static collection for `direct_fire` and otherwise uses the size-scoped
  // param GET (js:1367-1372).
  const firingModeRows = isDirectFire ? rowsOf(firingModeQ) : rowsOf(firingModeParamQ);

  /* ---- DOM-default selection (legacy parity) -----------------------------
   *
   * Every legacy `.search_field` <select> is a bare `{{#each}}` of options
   * with NO placeholder (gas_heater.selector.tmpl:129-860). With nothing
   * marked `selected` the browser selects the FIRST option, so the field both
   * DISPLAYS that value and — because collectData() reads `elem.val()`
   * (js:2506-2573) — SENDS it in the availability-count query.
   *
   * The port instead injects a synthetic `<option value="" />` whenever the
   * model carries no value for the field (DocSelect/ParamSelect above). Those
   * selects therefore rendered blank AND dropped out of the count query, so an
   * unrated heater reported the count of a far wider catalogue slice than
   * legacy: on dev3's AHU 6598 the query went out as
   *   elevation/mfg/type/series/power/heatex_mtl/qty_wide/qty_high  -> 3,172
   * where legacy additionally sends conn_hand, certification, fuel_type,
   * firing_mode, burner_mtl, tube_rows and configuration, cutting it to a
   * couple of dozen.
   *
   * Mirror the DOM: once a list has resolved, an EMPTY field adopts its first
   * option. Two exclusions, both from the legacy markup:
   *   - heatex Qty is the one select legacy renders with a placeholder
   *     (`<option value="">- -</option>`, tmpl:390), so empty stays empty;
   *   - Connection Hand renders with no options at all while qty_wide == 2
   *     (`{{#unless moreThanOne}}`, tmpl:296-301), so it stays empty too —
   *     ghCountFilters already drops it from the query in that case.
   * A field that HAS a value is never touched, even if the value is absent
   * from the freshly loaded list: the port deliberately keeps a saved
   * selection visible through the load window (the trailing synthetic
   * <option> in both select helpers), and clobbering it would lose a rated
   * heater's own parameters.
   *
   * This runs on every render rather than on a dep list because the param
   * chain resolves as a cascade (type -> series -> heatex/venting/fuel/... ),
   * exactly as legacy's DOM fills in list by list; it is a no-op once every
   * resolved list has been consumed. */
  const domDefaults: Array<[string, OptionRow[], boolean?]> = [
    ['mfg_id', docList('mfg_id')],
    ['type_id', rowsOf(typeListQ)],
    ['series_id', rowsOf(seriesListQ)],
    ['power_id', voltageRows],
    ['weather_rating_id', docList('weather_rating_id')],
    ['certification_id', docList('certification_id')],
    ['venting_id', ventingRows],
    ['conn_hand_id', Number(s.qty_wide) === 2 ? [] : docList('conn_hand_id')],
    ['air_stream_id', docList('air_stream_id')],
    ['blank_off_id', docList('blank_off_id')],
    ['burner_mtl_id', rowsOf(burnerMtlQ)],
    ['fuel_type_id', rowsOf(fuelTypeQ)],
    ['firing_mode_id', firingModeRows],
    ['pilot_id', rowsOf(pilotQ)],
    ['qty_wide', qtyWideRows, true],
    ['qty_high', qtyHighRows, true],
    // NB no `output_rating_std` here — "fill only when empty" is the wrong rule
    // for that one; see the calcRating effect below.
    // the Heat Exchanger sub-tab exists only for a non-direct-fire heater
    // (`{{#unless isDirectFire}}`, tmpl:111-115) — legacy has no such selects
    // in the DOM for type 3, so there is nothing to default.
    ...(isDirectFire
      ? []
      : ([
          ['heatex_mtl_id', rowsOf(heatexMtlQ)],
          ['tube_rows_vertically', rowsOf(tubeRowsQ), true],
          ['configuration', rowsOf(configurationQ), true],
        ] as Array<[string, OptionRow[], boolean?]>)),
  ];

  useEffect(() => {
    if (seeded.current !== modelId) return;
    let touched = false;
    const next = { ...state.current };
    for (const [name, rows, scalar] of domDefaults) {
      const current = next[name];
      if (current != null && current !== '') continue;
      const first = rows[0];
      if (first == null) continue;
      const value = scalar ? first : (first as Record<string, unknown>).id;
      if (value == null || value === '') continue;
      next[name] = value;
      touched = true;
    }
    if (!touched) return;
    // no markChanged()/setRated(null): legacy's default selection is the
    // browser picking option 0, which fires no change event and leaves the
    // rendered outputs alone.
    state.current = next;
    bump();
  });

  /* ---- re-cascade Type and Series (getCollections, js:507-531) ------------
   *
   * "Adopt the first option when the field is EMPTY" is enough for a fresh
   * dialog but not for a Manufacturer change, where the field is not empty —
   * it holds the previous maker's value. Legacy re-runs getCollections on every
   * `.params_gas_heater` change and re-resolves the head of the chain there:
   *
   *   types  — `if (types.length) type_id = types[0].id`   (unconditional)
   *   series — keep the saved id only `if (series.find(el => el.id == saved))`,
   *            else `series[0].id` (legacy's extra `!seriesIdChange` guard is
   *            a no-op here: a value the user just picked from the select is by
   *            construction in the list the select was built from)
   *
   * Every list below Series is keyed on the pair, so re-picking these two is
   * what pulls the rest of the cascade across: dev's Heatco→Midco click walks
   * type 1 → 3 and then series 296 → 1068, and only then fetches pilot_config /
   * burner_mtl / burner_config for the combination that exists.
   *
   * Applied as keep-else-first rather than legacy's unconditional `types[0]`
   * for Type: every maker in the catalogue offers exactly one gas-heater type,
   * so the two rules agree on real data, and keep-else-first additionally
   * leaves a Type the user picked himself alone. Skipped while the GET is in
   * flight so a stale list can never clobber a value the new one does offer. */
  useEffect(() => {
    if (seeded.current !== modelId) return;
    const next = { ...state.current };
    let touched = false;
    const keepElseFirst = (name: string, q: { data?: unknown; isFetching: boolean }) => {
      if (q.isFetching) return;
      const rows = rowsOf(q);
      if (!rows.length) return;
      const v = next[name];
      if (v != null && v !== '' && rows.some((r) => String(r.id) === String(v))) return;
      next[name] = rows[0].id;
      touched = true;
    };
    keepElseFirst('type_id', typeListQ);
    keepElseFirst('series_id', seriesListQ);
    if (!touched) return;
    state.current = next;
    bump();
  });

  /* ---- calcRating (js:1424-1495) -----------------------------------------
   *
   * The size list is AUTHORITATIVE, which is what makes it different from
   * every other select in this dialog. Legacy:
   *
   *   if (only.length == 1 || (only.length && !(selected && only.includes(+selected))))
   *       params.output_rating_std = params.output_rating_std_total = only[0]
   *
   * — i.e. a single-entry list is always adopted, and a selection that is not
   * in the freshly loaded list is REPLACED rather than kept. The port only had
   * the DOM-default rule ("adopt the first option when the field is empty"),
   * so once a size was saved it survived every capacity change even when it
   * was no longer offered.
   *
   * Legacy then re-scopes Tube Rows and Firing Mode to the chosen size
   * (js:1460-1490), keeping the current value when the new list still has it
   * and falling back to that list's first entry when it does not. Their
   * queries above are keyed on the live size, so this is the same
   * keep-else-first rule applied to their results.
   *
   * mfg 188 returns before any of it (js:1426) — the row is hidden there. */
  useEffect(() => {
    if (seeded.current !== modelId) return;
    if (isMfg188) return;
    if (!outputRatingValues.length) return;
    const next = { ...state.current };
    let touched = false;

    const cur = next.output_rating_std;
    if (
      cur == null ||
      cur === '' ||
      !outputRatingValues.includes(Number(cur))
    ) {
      next.output_rating_std = outputRatingValues[0];
      touched = true;
    }

    // keep-else-first on the two size-scoped lists. Skipped while their GETs
    // are still in flight for the newly chosen size, so a stale list can never
    // clobber a value that the new one does offer.
    const keepElseFirst = (name: string, values: Array<string | number>) => {
      if (!values.length) return;
      const v = next[name];
      if (v != null && v !== '' && values.some((x) => Number(x) === Number(v))) return;
      next[name] = values[0];
      touched = true;
    };
    if (!isDirectFire && !tubeRowsQ.isFetching) {
      keepElseFirst(
        'tube_rows_vertically',
        rowsOf(tubeRowsQ) as unknown as Array<string | number>
      );
    }
    if (!firingModeParamQ.isFetching) {
      keepElseFirst(
        'firing_mode_id',
        firingModeRows.map((r) => (r as Record<string, unknown>).id as number)
      );
    }

    if (!touched) return;
    state.current = next;
    bump();
  });

  if (
    detailLoading ||
    ghDocLoading ||
    globalDocLoading ||
    catalogsLoading ||
    seeded.current !== modelId
  ) {
    return <div className="preloader dotted-preloader active" />;
  }

  return (
    // `#gas_heater_new` and `#for-output-rendering` are SIBLINGS on dev
    // (gas_heater.selector.tmpl:1336-1338 — the input pane's div closes before
    // the output node opens), both direct children of
    // `#rating_mode_gas_heater_content`. The port nested the output node INSIDE
    // the input pane, which put the whole output sheet under two rules meant
    // only for the input half (rating_mode_gas_heater.scss):
    //   #gas_heater_new { height: 331px }
    //   #gas_heater_new .input-part { padding-left: 0 !important }
    // — so every output row's value column lost its 15px gutter and printed
    // flush against the unit ("btu/hr120,000", "in0.75"), and the sheet's own
    // `height: calc(100% - 345px)` resolved against 331px instead of the
    // dialog, collapsing the bordered box into a scroller. Keep them siblings.
    //
    // They sit inside the Backbone VIEW ELEMENT, not directly in the content
    // div: component_selector.js:169 does
    // `$('#rating_mode_gas_heater_content').html(view.$el)`, and view.$el is
    // Backbone's default bare <div> (the view's own `className` is commented
    // out, gas_heater.selector.js:95). That anonymous div is what
    // `#rating_mode_gas_heater_content > div { height: 100% }` targets — with
    // the two nodes returned bare, that rule would hit BOTH of them, stretch
    // the input pane to the full dialog and push the output sheet off the
    // bottom. So keep the wrapper.
    <div>
      <div
        id="gas_heater_new"
        className="gas_heater input-part gas_heater-rating-mode rating"
      >
      {/* ===== Input tab nav (tmpl:2-29) =====
          `input-menu` belongs on the <ul> INSIDE `.col-xs-1.nav.nav-tabs >
          .row`, not on the outer div — `#gas_heater_new .nav-tabs ul` and the
          `.input-menu` rules both key off that shape. */}
      <div className="col-xs-1 nav nav-tabs">
        <div className="row">
          <ul className="col-xs-12 input-menu">
            <li className="cursor-default">Input</li>
            {inputTabs.map((t) => (
              <li
                key={t}
                className={`${PANE_ID[t]} input_label${
                  activeTab === t ? ' selected active' : ''
                }${t === 'Options' ? ' non-clickable' : ''}`}
              >
                <a
                  data-toggle="tab"
                  href={`#${PANE_ID[t]}`}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab(t);
                  }}
                >
                  {t}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="col-xs-11 content-data tab-content">
        <div className="row">
        {/* ===== availability count badge (tmpl:33-35) =====
            Legacy renders this INSIDE `.col-xs-11.content-data`, as the first
            child, i.e. at the top of the content column — not above the tab
            nav, which is where the React port had it. Position aside, that
            placement is load-bearing: the zero-results colour comes from
            `#gas_heater_new .content-data .no-avaible-data { color:#dc362e }`
            (rating_mode_gas_heater.scss:523), which cannot match a node
            outside `.content-data`.

            `no-avaible-data` is legacy's spelling (missing the second "a") and
            has to stay misspelt to match that selector.

            mfg 188: legacy keeps the node and adds `.hide`; rendering nothing
            is equivalent here and is what the port already did. */}
        {!isMfg188 && (
          <div
            className={`col-xs-12 gas_heater-count${ghCount ? '' : ' no-avaible-data'}`}
          >
            <span id="qty">{ghCount ?? ''}</span> /{' '}
            <span id="all_count">{ghAllCount ?? ''}</span>
          </div>
        )}

        {/* ----- Heater (Gas_Heater_Properties) -----
            The port flattened legacy's THREE sub-tabs (General / Burner / Heat
            Exchanger — tmpl:101-674) into one 12-row pane, which is why dev3
            showed a single long column where dev shows a General tab and a
            `.more_tabing` strip in the top-right of the content box. Restored
            here: `.sub-tab-menu` + `.more_tabing` (absolutely positioned at
            top:-41px right:10px, so it lands on the count-badge line) and
            `.more-tab-content`, whose `> div.tab-pane{display:none}` /
            `.active{display:block}` (common.css:21191) does the switching. */}
        <div className={tabClass('Heater')} id={PANE_ID.Heater} data-name={PANE_ID.Heater}>
          <div className="col-xs-12 sub-tab-menu nav nav-tabs">
            <div className="row">
              <div className="col-xs-12">
                <ul className="col-xs-12 text-right more_tabing" style={{ height: '29px' }}>
                  <li
                    className={`properties_more properties-tab${
                      activeSubTab === 'properties_more' ? ' in active' : ''
                    }`}
                  >
                    <a
                      tabIndex={-1}
                      data-toggle="tab"
                      href="#properties_more"
                      onClick={(e) => {
                        e.preventDefault();
                        setActiveSubTab('properties_more');
                      }}
                    >
                      {'General '}
                    </a>
                  </li>
                  <li
                    className={`burner_more burner-tab${
                      activeSubTab === 'burner_more' ? ' in active' : ''
                    }`}
                  >
                    <a
                      id="render_burner"
                      tabIndex={-1}
                      data-toggle="tab"
                      data-tab="burner"
                      href="#burner_more"
                      onClick={(e) => {
                        e.preventDefault();
                        setActiveSubTab('burner_more');
                        // `click #render_burner: rateBurner` (js:119). This is
                        // the one write the port fires from something other
                        // than the #rate/#select/#print buttons, and it has to
                        // be: the Burner Assembly list is scoped by the
                        // burner_length_required_min the server computes during
                        // that PATCH, so there is no read-only way to fill it.
                        // Direct fire only, and a no-op when nothing the list
                        // depends on has changed (`burner_data_change`,
                        // js:804) — so re-opening the tab does not re-PATCH.
                        void rateBurner();
                      }}
                    >
                      {' Burner '}
                    </a>
                  </li>
                  {!isDirectFire && (
                    <li
                      className={`heat_exchanger_more heat_exchanger-tab${
                        activeSubTab === 'heat_exchanger_more' ? ' in active' : ''
                      }`}
                    >
                      <a
                        tabIndex={-1}
                        data-toggle="tab"
                        href="#heat_exchanger_more"
                        className={isMfg188 ? 'non-clickable' : undefined}
                        onClick={(e) => {
                          e.preventDefault();
                          if (isMfg188) return;
                          setActiveSubTab('heat_exchanger_more');
                        }}
                      >
                        {'Heat Exchanger '}
                      </a>
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>

          <div className="more-tab-content">
            {/* --- General (tmpl:121-304) --- */}
            <div className={subTabClass('properties_more')} id="properties_more">
              <div className="col-xs-12 col-md-12 col-lg-6">
                <Row label="Manufacturer" dataName="mfg_id" emptyOptions={!docList('mfg_id').length}>
                  {docSelect({ name: 'mfg_id' })}
                </Row>
                <Row label="Type" dataName="type_id" emptyOptions={!rowsOf(typeListQ).length}>
                  {paramSelect({ name: 'type_id', rows: rowsOf(typeListQ), labelKey: 'name' })}
                </Row>
                <Row label="Series" dataName="series_id" emptyOptions={!rowsOf(seriesListQ).length}>
                  {paramSelect({
                    name: 'series_id',
                    rows: rowsOf(seriesListQ),
                    labelKey: 'series',
                  })}
                </Row>
                <Row label="Voltage" dataName="power_id" emptyOptions={!voltageRows.length}>
                  {paramSelect({ name: 'power_id', rows: voltageRows, labelKey: 'name' })}
                </Row>
                <Row
                  label="Weather Rating"
                  dataName="weather_rating_id"
                  emptyOptions={!docList('weather_rating_id').length}
                >
                  {docSelect({ name: 'weather_rating_id' })}
                </Row>
                <Row
                  label="Certification"
                  dataName="certification_id"
                  emptyOptions={!docList('certification_id').length}
                >
                  {docSelect({ name: 'certification_id' })}
                </Row>
              </div>
              <div className="col-xs-12 col-md-12 col-lg-6">
                <Row label="Venting" dataName="venting_id" emptyOptions={!ventingRows.length}>
                  {paramSelect({ name: 'venting_id', rows: ventingRows, labelKey: 'name' })}
                </Row>
                <Row
                  label="Connection Hand"
                  dataName="conn_hand_id"
                  emptyOptions={!docList('conn_hand_id').length}
                >
                  {docSelect({ name: 'conn_hand_id' })}
                </Row>
              </div>
            </div>

            {/* --- Heat Exchanger (tmpl:305-435) --- */}
            {!isDirectFire && (
              <div className={subTabClass('heat_exchanger_more')} id="heat_exchanger_more">
                <div className="col-xs-12 col-md-12 col-lg-6">
                  <Row
                    label="Material"
                    dataName="heatex_mtl_id"
                    emptyOptions={!rowsOf(heatexMtlQ).length}
                  >
                    {paramSelect({
                      name: 'heatex_mtl_id',
                      rows: rowsOf(heatexMtlQ),
                      labelKey: 'name',
                      id: 'material_id',
                    })}
                  </Row>
                  <Row
                    label="Tube Vertical Rows"
                    dataName="tube_rows_vertically"
                    emptyOptions={!rowsOf(tubeRowsQ).length}
                  >
                    {paramSelect({
                      name: 'tube_rows_vertically',
                      rows: rowsOf(tubeRowsQ),
                      scalar: true,
                      id: 'tube_rows_vertically_id',
                    })}
                  </Row>
                  {/* legacy keeps the select in the DOM but hides the row
                      (`class="row hide"`, tmpl:365) — the picker below is the
                      real control and writes through to this field. */}
                  <Row
                    label="Configuration"
                    dataName="configuration"
                    rowClass="hide"
                    emptyOptions={!rowsOf(configurationQ).length}
                  >
                    {paramSelect({
                      name: 'configuration',
                      rows: rowsOf(configurationQ),
                      scalar: true,
                      id: 'configuration',
                    })}
                  </Row>
                  {/* no `emptyOptions`: legacy hardcodes the "- -" option
                      (tmpl:390), so `has('option')` is true even on an empty
                      heatexQty and this row never greys out on dev. */}
                  <Row label="Qty" dataName="heatex_qty_id">
                    {paramSelect({
                      name: 'heatex_qty_id',
                      rows: rowsOf(heatexQtyQ),
                      scalar: true,
                      id: 'heatex_qty_id',
                      emptyOption: '- -',
                    })}
                  </Row>
                </div>
                {/* the configuration picker (tmpl:401-434). Two sibling divs:
                    the scrolling letter/airflow table, then the absolutely
                    positioned image plate — only the `.selected` image is
                    displayed (rating_mode_gas_heater.scss:255-300). */}
                <div className="col-xs-6 heat_exchanger_more_configuration_row">
                  <div>
                    <div className="configuration-row header-row">
                      <div className="configuration-item">Configuration</div>
                      <div className="configuration-airflow">Airflow</div>
                      <div className="configuration-img" />
                    </div>
                    <div id="heat_exchanger_more_configuration">
                      {configurationLetters.map((letter, index) => (
                        <div
                          key={letter}
                          className={`configuration-letter-row configuration-row configuration-item-row${
                            letter === selectedConfiguration ? ' selected' : ''
                          }`}
                          data-value={letter}
                          data-index={index}
                        >
                          <div
                            className="configuration-row-wrap"
                            data-value={letter}
                            data-index={index}
                            onClick={() => onParamField('configuration', letter)}
                          >
                            <div className="configuration-item">{letter}</div>
                            <div className={`configuration-airflow airflow-${letter}`}>
                              <div className="arrow-box" />
                              <div className="l-arrow-box">
                                <div className="l-arrow-box-top" />
                                <div className="l-arrow-box-right" />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    {configurationLetters.map((letter, index) => (
                      <div
                        key={letter}
                        className={`configuration-img configuration-letter-row configuration-${letter}${
                          letter === selectedConfiguration ? ' selected' : ''
                        }`}
                        data-value={letter}
                        data-index={index}
                      >
                        <span className="config-letter">{letter}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* --- Burner (tmpl:437-670) --- */}
            <div className={subTabClass('burner_more')} id="burner_more">
              <div className="col-xs-12 col-md-12 col-lg-6">
                {/* legacy's label is an EMPTY span followed by the bare text
                    " Material", and the whole row is `.non-clickable`. */}
                <Row label="" dataName="burner_mtl_id" rowClass="non-clickable" labelSuffix=" Material">
                  {paramSelect({
                    name: 'burner_mtl_id',
                    rows: rowsOf(burnerMtlQ),
                    labelKey: 'name',
                    id: 'burner_mtl_id',
                  })}
                </Row>
                <Row
                  label="Fuel Type"
                  dataName="fuel_type_id"
                  emptyOptions={!rowsOf(fuelTypeQ).length}
                >
                  {paramSelect({
                    name: 'fuel_type_id',
                    rows: rowsOf(fuelTypeQ),
                    labelKey: 'name',
                    id: 'fuel_type_id',
                  })}
                </Row>
                <Row
                  label="Firing Mode"
                  dataName="firing_mode_id"
                  emptyOptions={!firingModeRows.length}
                >
                  {paramSelect({
                    name: 'firing_mode_id',
                    rows: firingModeRows,
                    labelKey: 'name',
                    id: 'firing_mode_id',
                  })}
                </Row>
                <Row label="Gas Inlet Pressure" dataName="gas_inlet_pressure" unit="in WC" unitClass="uom right">
                  {numInput({
                    name: 'gas_inlet_pressure',
                    formatter: 'decimal',
                    fieldClass: 'input_gas_heater model_field gas_inlet_pressure small-input',
                  })}
                </Row>
                <Row
                  label="Manifold Pressure"
                  dataName="burner_manifold_pressure_conditional"
                  unit="in WC"
                  unitClass="uom right"
                  required
                >
                  {numInput({
                    name: 'burner_manifold_pressure_conditional',
                    id: 'burner_manifold_pressure_conditional',
                    formatter: 'decimal',
                    ...ghManifoldPressureInputProps(s.fuel_type_id),
                    fieldClass: 'model_field input_gas_heater small-input',
                  })}
                </Row>
                <Row label="Pilot" dataName="pilot_id" emptyOptions={!rowsOf(pilotQ).length}>
                  {paramSelect({
                    name: 'pilot_id',
                    rows: rowsOf(pilotQ),
                    labelKey: 'name',
                    id: 'pilot_id',
                  })}
                </Row>
              </div>
              {/* the burner-assembly column. `#assy_config` carries `.hide`
                  for a non-type-3 heater and
                  `#assy_config.hide + #burner_config{display:none}`
                  (rating_mode_gas_heater.scss:579) takes the Burner box down
                  with it — but showBurnerAssy() drops the class again for
                  `#type_id == 3` on every render (js:1387-1394), so a
                  direct-fire heater shows BOTH boxes on dev. */}
              <div className="col-xs-12 col-md-12 col-lg-6">
                <div className="burner-content">
                  <div
                    className={`col-xs-5 border-box${isDirectFire ? '' : ' hide'}`}
                    id="assy_config"
                  >
                    {isDirectFire && (
                      <>
                        <span
                          className="list-title"
                          name="assy_config_id"
                          data-name="assy_config_id"
                        >
                          Burner Assembly
                        </span>
                        {/* a `multiple` select used as a list box, exactly as
                            legacy (burner_assy_content.tmpl) — the styling
                            (#burner_assy_config, scss:582) depends on it. */}
                        <select
                          id="burner_assy_config"
                          multiple
                          className="params_gas_heater"
                          name="assy_config_id"
                          value={[String(s.assy_config_id ?? '')]}
                          onChange={(e) => {
                            // legacy changeParams takes val()[0] off the
                            // multiple select (js:2192).
                            const picked = Array.from(e.target.selectedOptions)[0]?.value;
                            if (picked == null) return;
                            state.current = {
                              ...state.current,
                              assy_config_id: Number(picked),
                            };
                            setBurnerRows([]);
                            bump();
                          }}
                        >
                          {burnerAssyRows.map((a) => (
                            <option
                              key={String(a.id)}
                              value={String(a.id)}
                              data-value={String(a.value ?? '')}
                            >
                              {String(a.name ?? '')}
                            </option>
                          ))}
                        </select>
                      </>
                    )}
                  </div>
                  <div
                    className={`col-xs-6 border-box${isUserDefinedAssy ? '' : ' static'}`}
                    id="burner_config"
                  >
                    <div className="row">
                      <div className="col-xs-6">
                        <span className="list-title">Burner </span>
                      </div>
                      <div className="col-xs-6">
                        <button
                          className="add_burner"
                          id="add_burner"
                          type="button"
                          onClick={addBurner}
                        >
                          + ADD
                        </button>
                      </div>
                    </div>
                    <div id="burner_config_content">
                      {burnerRows.map((row) => (
                        <div
                          className={`col-xs-12${row.id == null ? ' burner_row' : ''}`}
                          key={row.key}
                          id={row.id == null ? undefined : String(row.id)}
                        >
                          <select
                            name="burner_config_id_list"
                            className="burner_config_id_list params_gas_heater"
                            value={row.burner_config_id == null ? '' : String(row.burner_config_id)}
                            onChange={(e) => {
                              const v = num(e.target.value);
                              setBurnerRows((rows) =>
                                rows.map((r) =>
                                  r.key === row.key ? { ...r, burner_config_id: v as number } : r
                                )
                              );
                              setRated(null);
                              setRatedRow(null);
                              setRatedCount(0);
                              markChanged();
                            }}
                          >
                            {row.burner_config_id == null && <option value="" />}
                            {rowsOf(burnerConfigQ).map((r) => (
                              <option key={String(r.id)} value={String(r.id)}>
                                {optionLabel(r, 'name')}
                              </option>
                            ))}
                          </select>
                          <div className="dropdown dropdown_burner">
                            <div>
                              <button
                                className="ka-has-tooltip btn btn-burner-delete"
                                type="button"
                                tooltip="Delete"
                                aria-expanded="false"
                              >
                                <a
                                  tabIndex={-1}
                                  href="#"
                                  className="remove"
                                  name="confirm_delete"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    void removeBurner(row);
                                  }}
                                >
                                  <i className="icon-trash" />
                                </a>
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* the non-User-Defined branch: the chosen assembly's own
                        burner_config list, read-only (js:948-957). `.static` on
                        the box is what makes it so. */}
                    <div id="burner_config_content_static">
                      {staticBurnerConfigs.map((c, i) => (
                        <div className="col-xs-12" key={`${String(c.id)}-${i}`}>
                          <select
                            name="burner_config_id_list"
                            className="params_gas_heater"
                            value={String(c.id)}
                            onChange={() => undefined}
                          >
                            <option value={String(c.id)}>{String(c.name ?? '')}</option>
                          </select>
                          <div className="dropdown dropdown_burner" />
                        </div>
                      ))}
                    </div>
                    <div className="row" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ----- Performance ----- */}
        {/* J6 — data-season stamped like legacy (tmpl:675). The legacy
            `.season-group-winter` wrapper around the EADB + Desired-LADB
            rows is NOT ported (survey §B6: with data-season="summer" it
            hides both REQUIRED rows — a real UX bug; dead here). */}
        <div
          className={tabClass('Performance')}
          id="ahu_Performance"
          data-season={deriveSeasonValue(
            globalDoc as ICmsAhuDoc | undefined,
            seasonIdRef.current
          )}
        >
          <div className="col-xs-12 col-md-12 col-lg-6">
            {/* `data-type="number"` (tmpl:686) is the whole of legacy's
                right-justification here — this field carries no `text-right`. */}
            <Row label="Airflow total" dataName="airflow_actual" unit="CFM" required>
              {numInput({ name: 'airflow_actual', formatter: 'integer', dataType: 'number' })}
            </Row>
            <Row
              label="Air Stream"
              dataName="air_stream_id"
              emptyOptions={!docList('air_stream_id').length}
            >
              {docSelect({
                name: 'air_stream_id',
                fieldClass: 'select_gas_heater model_field collections_change_field',
              })}
            </Row>
            {/* Altitude (tmpl:709-722) — the project's elevation, read-only.
                Dropped by the port; dev shows it under Air Stream. See
                `projectElevation` above for why this is never blank. */}
            <Row label="Altitude" dataName="air_stream_id">
              <input
                name="elevation"
                className="input_gas_heater project_field"
                type="text"
                data-type="number"
                readOnly
                tabIndex={-1}
                value={String(projectElevation)}
                onChange={() => undefined}
              />
            </Row>
          </div>
          <div className="col-xs-12 col-md-12 col-lg-6">
            {/* J6 — the Season select (tmpl:741-752): options from the CMS
                payload's season_by_component['Gas Heater'] (Winter-only as
                seeded), NO None option; selection stages on seasonIdRef
                (changeSeasonField js:1627-1634) and persists ONLY via
                doSelect. With nothing staged the browser shows the first
                option while the staged value stays null — legacy renders no
                `selected` attr in that state, same visible result. */}
            <Row
              label="Season"
              dataName="season_id"
              labelCols={6}
              emptyOptions={
                !cmsSeasonsByComponent(globalDoc as ICmsAhuDoc | undefined, 'Gas Heater').length
              }
            >
              <select
                name="season_id"
                className="input_gas_heater"
                value={(() => {
                  const rows = cmsSeasonsByComponent(
                    globalDoc as ICmsAhuDoc | undefined,
                    'Gas Heater'
                  );
                  const staged = seasonIdRef.current;
                  const hit = rows.find((r) => Number(r.id) === Number(staged));
                  return hit ? String(hit.id) : String(rows[0]?.id ?? '');
                })()}
                onChange={(e) => {
                  // legacy changeSeasonField: stage + re-render ONLY (no
                  // modelFieldChanged, no dirty-marking — js:1627-1634).
                  seasonIdRef.current = parseInt(e.target.value, 10) || null;
                  bump();
                }}
              >
                {cmsSeasonsByComponent(
                  globalDoc as ICmsAhuDoc | undefined,
                  'Gas Heater'
                ).map((r) => (
                  <option key={String(r.id)} value={String(r.id)}>
                    {String(r.name ?? '')}
                  </option>
                ))}
              </select>
            </Row>
            {/* the label is "Entering Air Temperature", not "… (EADB)", and the
                unit span is `uom temp right` — `.temp::before{content:'o'}`
                (rating_mode_gas_heater.scss:424) is what draws the degree
                mark, so `percent right` printed a bare "F" on dev3. */}
            <Row
              label="Entering Air Temperature"
              dataName="eadb_temp"
              unit="F"
              unitClass="uom temp right"
              labelCols={6}
              required
            >
              {numInput({
                name: 'eadb_temp',
                formatter: 'decimal',
                dataMax: GH_EADB_TEMP_MAX,
                fieldClass: 'model_field input_gas_heater eadb_temp text-right small-input',
              })}
            </Row>
            <Row
              label="Desired Leaving Air Temp"
              dataName="ladb_temp_desired"
              unit="F"
              unitClass="uom temp right"
              labelCols={6}
              required
            >
              {numInput({
                name: 'ladb_temp_desired',
                formatter: 'decimal',
                dataMin: GH_LADB_TEMP_DESIRED_MIN,
                dataMax: GH_LADB_TEMP_DESIRED_MAX,
                fieldClass: 'text-right model_field input_gas_heater',
              })}
            </Row>
            <Row
              label="Capacity Required total "
              dataName="capacity_required_total"
              unit="btu/hr"
              unitClass="uom right"
              labelCols={6}
              required
            >
              {/* legacy computes this client-side and paints it on render
                  (js:1139-1141 / :1340) — `Math.round(airflow*(ladb-eadb)
                  *1.085)`. The port left it bound to a state key nothing ever
                  sets, so dev3 showed it permanently blank. */}
              <input
                id="capacity_required_total"
                readOnly
                autoComplete="off"
                className="non-clickable text-right model_field input_gas_heater"
                type="text"
                data-formatter_type="integer"
                name="capacity_required_total"
                value={
                  capacityRequiredTotal == null
                    ? ''
                    : formatIntegerDisplay(capacityRequiredTotal)
                }
                onChange={() => undefined}
              />
            </Row>
            {/* Qty Wide x High is ONE row in legacy (tmpl:797-820) with two
                half-width selects and a bare "x" between them — the port split
                it into a "Qty Wide" row and a "High" row.

                No `emptyOptions`: ids `wide` and `high` are two of the three
                legacy exempts from the non-clickable sweep (js:1131).

                The `{' '}` either side of the "x" are the legacy template's
                whitespace text nodes (tmpl:807-810 breaks the line after each
                `</select>`, which HTML collapses to a single space). JSX drops
                newline whitespace between elements, so without them the "x"
                butts against the Wide select's arrow and the High select sits
                ~8px to the left of where dev draws it. `span.unit` computes to
                `display:inline`, so its inline `width:9px` is inert — the two
                spaces ARE the gap. */}
            <Row
              label="Qty Wide x"
              dataName="qty_wide"
              labelCols={6}
              labelSuffixName="qty_high"
              labelSuffix=" High"
            >
              <>
                {paramSelect({
                  name: 'qty_wide',
                  rows: qtyWideRows,
                  scalar: true,
                  id: 'wide',
                  fieldClass: 'search_field params_gas_heater collections_change_field non-empty',
                  style: { width: 'calc(50% - 32.5px)' },
                })}{' '}
                <span className="unit not-uom " style={{ width: '9px' }}>
                  x
                </span>{' '}
                {paramSelect({
                  name: 'qty_high',
                  rows: qtyHighRows,
                  scalar: true,
                  id: 'high',
                  fieldClass: 'search_field params_gas_heater collections_change_field',
                  style: { width: 'calc(50% - 32.5px)' },
                })}
              </>
            </Row>
            {/* no `emptyOptions`: id `input_rating_std` is the third legacy
                exempt (js:1131) — an unrated heater has no sizes yet and legacy
                deliberately leaves the row live so a Rate can fill it. */}
            <Row
              label="Available Heater Sizes"
              dataName="output_rating_std"
              labelCols={6}
              rowClass={isMfg188 ? 'hide' : undefined}
              inputPartId="input_output_rating_std"
            >
              {noHeaterSizes ? (
                /* the capacity query answered with no sizes — legacy replaces
                   the select outright (js:1448-1450). */
                <input
                  id="output_rating_std"
                  name="output_rating_std"
                  data-name="output_rating_std"
                  type="text"
                  autoComplete="off"
                  readOnly
                  className="input_gas_heater text-right"
                  value="none"
                  onChange={() => undefined}
                />
              ) : (
                paramSelect({
                  name: 'output_rating_std',
                  rows: outputRatingRows,
                  labelKey: 'name',
                  strictRows: true,
                  id: 'input_rating_std',
                  fieldClass: 'search_field select_gas_heater params_gas_heater',
                })
              )}
            </Row>
          </div>
        </div>

        {/* ----- Options (tmpl:878-983) -----
            Legacy's nav entry is `.non-clickable`, so the pane is unreachable;
            its selects are fed from `collections.rating_mfg`, which the legacy
            view never assigns — they render EMPTY there too. Ported as the
            same inert markup purely so the input menu matches. */}
        {!isDirectFire && (
          <div className={tabClass('Options')} id={PANE_ID.Options} data-name={PANE_ID.Options}>
            <div className="col-xs-12 col-md-12 col-lg-6">
              {[
                ['Housing', 'housing_id'],
                ['Vestibule', 'vestibule_id'],
                ['Shroud', 'shroud_id'],
                ['Rack', 'rackg_id'],
                ['Airside Enclosure', 'airside_enclosure_id'],
              ].map(([label, name]) => (
                <Row key={name} label={label} dataName={name} emptyOptions>
                  <select
                    name={name}
                    id={name}
                    data-name={name}
                    className="params_gas_heater collections_change_field"
                    value=""
                    onChange={() => undefined}
                  >
                    <option value="" />
                  </select>
                </Row>
              ))}
            </div>
          </div>
        )}

        {/* ----- Mounting (tmpl:985-1035) — 6/6 split, both geometry rows
            `required`, and the Assy-Distance row `.non-clickable`. ----- */}
        <div className={tabClass('Mounting')} id={PANE_ID.Mounting} data-name={PANE_ID.Mounting}>
          <div className="col-xs-12 col-md-12 col-lg-6">
            <Row
              label="Assy Height from floor"
              dataName="assy_height_from_floor"
              unit="in"
              labelCols={6}
              required
            >
              {/* `tabindex="-1"` (tmpl:995) — dev skips both geometry fields
                  when you Tab through Mounting and lands on Blank-Off. The
                  Distance field gets it via the read-only branch; this one is
                  editable, so it needs it explicitly. */}
              {numInput({
                name: 'assy_height_from_floor',
                formatter: 'decimal',
                skipTabStop: true,
                fieldClass: 'text-right input_gas_heater required_field model_field has_default',
              })}
            </Row>
            <Row
              label="Assy Distance from Left Wall"
              dataName="assy_distance_from_left_wall"
              unit="in"
              labelCols={6}
              rowClass="non-clickable"
              required
            >
              {numInput({
                name: 'assy_distance_from_left_wall',
                formatter: 'decimal',
                readOnly: true,
                fieldClass: 'text-right input_gas_heater required_field model_field has_default',
              })}
            </Row>
            <Row
              label="Blank-Off"
              dataName="blank_off_id"
              labelCols={6}
              emptyOptions={!docList('blank_off_id').length}
            >
              {docSelect({
                name: 'blank_off_id',
                fieldClass: 'select_gas_heater params_gas_heater collections_change_field',
              })}
            </Row>
          </div>
        </div>

        {/* ----- Bypass (tmpl:1037-1285) -----
            Placeholder markup in legacy too: every control is bound to an
            empty collection or has no name binding at all. Ported verbatim so
            the tab exists and lays out identically. */}
        {!isDirectFire && (
          <div className={tabClass('Bypass')} id={PANE_ID.Bypass} data-name={PANE_ID.Bypass}>
            <div className="col-xs-12 col-md-12 col-lg-6">
              <Row label="Type" dataName="housing_id" emptyOptions>
                <BypassSelect name="housing_id" />
              </Row>
              <Row label="Plane" dataName="vestibule_id" emptyOptions>
                <BypassSelect name="vestibule_id" />
              </Row>
              {[1, 2, 3, 4].map((n) => (
                <Row
                  key={n}
                  label={`Bypass-${n} W x H`}
                  dataName="airflow_actual"
                  unit="in"
                  emptyOptions
                >
                  <>
                    <BypassSelect name="airside_enclosure_id" width="108px" />{' '}
                    <span className="unit not-uom ">x</span>{' '}
                    <BypassSelect name="airside_enclosure_id" width="108px" />
                  </>
                </Row>
              ))}
            </div>
            <div className="col-xs-12 col-md-12 col-lg-6">
              <Row label="Airflow" dataName="airflow_actual_bypass" unit="CFM">
                <input
                  autoComplete="off"
                  className="text-right col-xs-12 gas_heater_field "
                  type="text"
                  data-empty="true"
                  data-formatter_type="integer"
                  name="airflow_actual_bypass"
                  style={{ width: '131px' }}
                  value=""
                  onChange={() => undefined}
                />
              </Row>
              {[0, 1, 2, 3].map((n) => (
                <Row
                  key={n}
                  label="H from floor | Dist. from LW"
                  dataName="height_from_floor"
                  unit="in"
                  rowClass={n === 0 ? 'top-margin' : undefined}
                >
                  <>
                    <BypassInput />{' '}
                    <span className="unit not-uom ">x</span>{' '}
                    <BypassInput />
                  </>
                </Row>
              ))}
            </div>
          </div>
        )}

        {/* ----- Pricing (tmpl:1287-1330) ----- */}
        <div className={tabClass('Pricing')} id={PANE_ID.Pricing} data-name={PANE_ID.Pricing}>
          <div className="col-xs-12 col-md-12 col-lg-6">
            <Row
              label="Method"
              dataName="pricing_method_id"
              emptyOptions={!rowsOf(pricingQ).length}
            >
              <select
                name="pricing_method_id"
                data-name="pricing_method_id"
                className="pricing_method_field model_field"
                value={s.pricing_method_id == null ? '' : String(s.pricing_method_id)}
                onChange={(e) => onModelField('pricing_method_id', e.target.value)}
              >
                <option value="" />
                {rowsOf(pricingQ).map((r) => (
                  <option key={String(r.id)} value={String(r.id)}>
                    {optionLabel(r, 'name')}
                  </option>
                ))}
              </select>
            </Row>
            {/* legacy is ONE row — "Gas Heater : Mtl | Freight" with the two
                inputs side by side in col-xs-6 halves — and it is hidden by
                class (not unmounted) when the method is List (id 1). The port
                had two stacked full-width rows. */}
            <div
              className={`pricing_method_input row${
                Number(s.pricing_method_id) === 1 ? ' hide' : ''
              }`}
            >
              <div className="col-xs-5 label-part">
                <span data-name="cost_product_user"> Gas Heater : Mtl </span>
                <span data-name="cost_freight_user"> | Freight</span>
                <span className="percent right">US$</span>
              </div>
              <div className="col-xs-7 input-part ">
                <div className="row">
                  <div className="col-xs-6">
                    {numInput({
                      name: 'cost_product_user',
                      formatter: 'decimal',
                      fieldClass: 'model_field coil_field',
                      style: { width: '100%' },
                    })}
                  </div>
                  <div className="col-xs-6">
                    {numInput({
                      name: 'cost_freight_user',
                      formatter: 'decimal',
                      fieldClass: 'model_field coil_field',
                      style: { width: '100%' },
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
      </div>

      {/* ===== OUTPUT (rated outputs — stored/initial state) =====
          legacy's node is `class="gas_heater col-xs-12"` (tmpl:1337); the port
          added `output-part`, which pulls in the `.output-part .unit,.percent
          {margin-top:0!important}` rule the legacy sheet never applied here. */}
      <div id="for-output-rendering" className="gas_heater col-xs-12">
        <GasHeaterRatedOutputs
          model={rated}
          ratedModel={ratedModel}
          ratedDatetime={ratedDatetime.current}
          ratedCount={ratedCount}
          activeIndex={ratedIndex}
          // legacy changeGasHeater: picking another result re-rates against
          // that catalog row (js:1981-1988).
          onSelectResult={(i) => {
            userArmed.current = true;
            saveAbort.current?.abort?.();
            void searchGasHeaters(catalog, i);
          }}
        />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// small presentational helpers
// ---------------------------------------------------------------------------

/**
 * One label/control row, shaped like the legacy template rather than like the
 * generic UvcSelector Row. Three things here are load-bearing:
 *
 *  - the control sits in `.input-part > .row > .col-xs-12`, not directly in
 *    `.input-part` (every legacy row does this);
 *  - the split is 5/7 on most tabs but 6/6 on Performance and Mounting, so it
 *    is a prop instead of a constant.
 *
 * (Legacy also puts a `name=` on the label span alongside `data-name=`. No
 * stylesheet selects on it — `span[name=…]` appears nowhere in common.css —
 * so it is left off rather than fought past React's span prop types.)
 */
const Row = ({
  label,
  dataName,
  unit,
  unitClass = 'percent right',
  required,
  labelCols = 5,
  rowClass,
  emptyOptions,
  inputPartId,
  labelSuffix,
  labelSuffixName,
  children,
}: {
  label: string;
  dataName: string;
  unit?: string;
  /** `percent right` | `uom right` | `uom temp right` (temp draws the degree). */
  unitClass?: string;
  required?: boolean;
  /** width of the label column; the input column takes the remainder. */
  labelCols?: 5 | 6;
  rowClass?: string;
  /** this row's select resolved to zero options — grey the whole row out, the
   *  way legacy's post-render `select[name]` sweep does (js:1128-1137; see the
   *  note above `docList`). */
  emptyOptions?: boolean;
  inputPartId?: string;
  /** trailing text after the label span (legacy's split "Qty Wide x"+" High"). */
  labelSuffix?: string;
  labelSuffixName?: string;
  children: React.ReactNode;
}) => (
  <div
    className={['row', rowClass, emptyOptions ? 'non-clickable' : undefined]
      .filter(Boolean)
      .join(' ')}
  >
    <div className={`col-xs-${labelCols} label-part`}>
      <span className={required ? 'required' : undefined} data-name={dataName}>
        {label}
      </span>
      {labelSuffix && <span data-name={labelSuffixName ?? dataName}>{labelSuffix}</span>}
      {unit && <span className={unitClass}>{unit}</span>}
    </div>
    <div className={`col-xs-${12 - labelCols} input-part`} id={inputPartId}>
      <div className="row">
        <div className="col-xs-12">{children}</div>
      </div>
    </div>
  </div>
);

/** An inert Bypass-tab select (legacy binds these to an empty collection).
 *
 * Type/Plane (no `width`) match legacy's `housing_id`/`vestibule_id` markup,
 * which carries `select_gas_heater` and no inline width — that class is the
 * ONLY rule giving an unsized select a width (see the `docSelect` note
 * above); without it an empty `<option value="">` select shrinks to its
 * smallest intrinsic width instead of filling the column. The four
 * `airside_enclosure_id` selects (Bypass-N, `width="108px"`) match legacy's
 * own fixed-width markup, which omits that class in favour of the inline
 * style — so the class is added only when no `width` is passed. */
const BypassSelect = ({ name, width }: { name: string; width?: string }) => (
  <select
    name={name}
    data-name={name}
    className={`${width ? '' : 'select_gas_heater '}params_gas_heater collections_change_field`}
    style={width ? { width } : undefined}
    value=""
    onChange={() => undefined}
  >
    <option value="" />
  </select>
);

/** An inert Bypass-tab geometry input (legacy has no value binding either). */
const BypassInput = () => (
  <input
    name="height_from_floor"
    type="text"
    data-empty="true"
    data-formatter_type="decimal"
    data-name="height_from_floor"
    className="model_field height_from_floor text-right "
    style={{ width: '131px' }}
    value=""
    onChange={() => undefined}
  />
);

export default GasHeaterSelector;
